import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, APP_INITIALIZER, NgModule } from '@angular/core';
import * as i1$1 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';
import * as i1$2 from '@ngrx/store';
import { createAction, props, createReducer, on, createFeatureSelector, createSelector, StoreModule } from '@ngrx/store';
import * as i1 from '@angular/common/http';
import { HttpParams } from '@angular/common/http';
import { throwError, of, combineLatest, timer, EMPTY, merge, concat } from 'rxjs';
import { catchError, concatMap, map, switchMap, withLatestFrom, filter, debounceTime, mergeMap, groupBy } from 'rxjs/operators';
import { createEntityAdapter } from '@ngrx/entity';
import { InventoryStatus, MigrationStatus } from '@usf/inventory-types';
import * as i2 from '@panamax/app-state';
import { selectShowInventory } from '@panamax/app-state';

const INVENTORY_KEY = 'inventory';
const INVENTORY_UI_CONSTANTS_KEY = 'inventory-ui-constants';
const FOOD_COST_KEY = 'food-cost';
const INVENTORY_STORAGE_KEYS = [
    INVENTORY_KEY,
    INVENTORY_UI_CONSTANTS_KEY,
    FOOD_COST_KEY,
];

const INVENTORY_ACTIONS = {
    resetStore: createAction('[NgRxInventory - Inventory] Reset inventories in store'),
    load: createAction('[NgRxInventory - Inventory] Load Inventory from Domain API'),
    loadWithAllDepartments: createAction('[NgRxInventory - Inventory] Load Inventory from Domain API with all Departments'),
    stripAdditionalDepartments: createAction('[NgRxInventory - Inventory] Strip Inventories with departments numbers other than one sent', props()),
    loadSuccess: createAction('[NgRxInventory - Inventory] Load Inventory from Domain API succeeded', props()),
    loadFail: createAction('[NgRxInventory - Inventory] Load Inventory from Domain API failed', props()),
    create: createAction('[NGRXTrace - Inventory] Create Inventory from Domain API', props()),
    createSuccess: createAction('[NGRXTrace - Inventory] Create Inventory from Domain API succeeded', props()),
    createFail: createAction('[NGRXTrace - Inventory] Create Inventory from Domain API failed', props()),
    update: createAction('[NgRxInventory - Inventory] Update Inventory from Domain API', props()),
    updateSuccess: createAction('[NgRxInventory - Inventory] Update Inventory from Domain API succeeded', props()),
    updateFail: createAction('[NgRxInventory - Inventory] Update Inventory from Domain API failed', props()),
    updateRecentInventoriesSort: createAction('[NgRxInventory - Inventory] Update sort direction of selected header and reset others to their default values in recentInventoriesSort', props()),
    resetRecentInventoriesSort: createAction('[NgRxInventory - Inventory] Resets recentInventoriesSort to its default value'),
    updateAllInventoriesSort: createAction('[NgRxInventory - Inventory] Update sort direction of selected header and reset others to their default values in allInventoriesSort', props()),
    resetAllInventoriesSort: createAction('[NgRxInventory - Inventory] Resets allInventoriesSort to its default value'),
    updateInventoryWorksheetMode: createAction('[NgRxInventory - Inventory] Update Inventory Worksheet mode to count or edit', props()),
    updateInventoryWorksheetCountSort: createAction('[NgRxInventory - Inventory] Update sort direction of selected header and reset others to their default values in the given inventoryWorksheetCountSort section', props()),
    resetInventoryWorksheetCountSort: createAction('[NgRxInventory - Inventory] Resets inventoryWorksheetCountSort to its default value for the given inventory id', props()),
    updateInventoryWorksheetEditSort: createAction('[NgRxInventory - Inventory] Update sort direction of selected header and reset others to their default values in the given inventoryWorksheetEditSort section', props()),
    resetInventoryWorksheetEditSort: createAction('[NgRxInventory - Inventory] Resets inventoryWorksheetEditSort to its default value for the given inventory id', props()),
    updateIsSelected: createAction('[NgRxInventory - Inventory] Update Inventory isSelected', props()),
    updateSelectedPrice: createAction('[NgRxInventory - Inventory] Update Inventory selected price', props()),
    updateInEditNameMode: createAction('[NgRxInventory - Inventory] Update Inventory options in edit name mode', props()),
    updateInEditGroupsMode: createAction('[NgRxInventory - Inventory] Update Inventory options in edit groups mode', props()),
    setNameValidator: createAction('[NgRxInventory - Inventory] Update name of inventory to check', props()),
    setDateValidator: createAction('[NgRxInventory - Inventory] Update date of invetnory to check', props()),
    editName: createAction('[NgRxInventory - Inventory] Edit Inventory name', props()),
    searchInInventoryWorksheet: createAction('[NGRXInventory - Inventory] - Search in Inventory Worksheet', props()),
    searchInAllInventories: createAction('[NGRXInventory - Inventory] - Search in All Inventories Page', props()),
    complete: createAction('[NgRxInventory - Inventory] Update Inventory completion time stamp from Domain API', props()),
    completeSuccess: createAction('[NgRxInventory - Inventory] Update Inventory completion time stamp from Domain API succeeded', props()),
    completeFail: createAction('[NgRxInventory - Inventory] Update Inventory completion time stamp from Domain API failed', props()),
    toggleShowCompleted: createAction('[NgRxInventory - Inventory] Toggle value for showing completed inventories on all inventory page', props()),
    resetAllDuplicatesFlag: createAction('[NgRxInventory - Inventory] Resets all duplicates to its default value'),
    updateWithToastMessageSuccess: createAction('[NGRXTrace - Inventory] Update Inventory with Toast Message from Domain API succeeded', props()),
    updateWithToastMessageFail: createAction('[NGRXTrace - Inventory] Update Inventory with Toast Message from Domain API failed', props()),
    resetProductsAddedInventoryOption: createAction('[NgRxInventory - Inventory] Reset products added in Inventory Options'),
    deleteSuccess: createAction('[NgRxInventory - Inventory] Delete an inventory worksheet success', props()),
    deleteFail: createAction('[NgRxInventory - Inventory] Delete an inventory worksheet failure', props()),
    delete: createAction('[NgRxInventory - Inventory] Delete an inventory worksheet', props()),
    updateInventoryInformationModal: createAction('[NgRxInventory - Inventory] Update open/close state for Inventory Information Modal', props()),
    updateInventorySnapshot: createAction('[NgRxInventory - Inventory] Saves a copy of the inventory in Inventory Options before an action is dispatched', props()),
    updateInventoryCustomUOMModal: createAction('[NgRxInventory - Inventory] Update open/close state for Inventory Custom UOM Modal', props()),
    updateWithTracingSuccess: createAction('[NGRXTrace - Inventory] Update Inventory with Tracing, Domain API succeeded', props()),
    updateWithTracingFail: createAction('[NGRXTrace - Inventory] Update Inventory with Tracing, Domain API failed', props()),
    displayInventory: createAction('[NGRXTrace - Inventory] Display Inventory', props()),
    displayInventorySuccess: createAction('[NGRXTrace - Inventory] Display Inventory Success', props()),
    displayInventoryFail: createAction('[NGRXTrace - Inventory] Display Inventory Failure', props()),
    enterOffline: createAction('[NgRxInventory - Inventory] Enter Offline'),
    exitOffline: createAction('[NgRxInventory - Inventory] Exit Offline', props()),
    setOnlineFlagWithoutMessage: createAction('[NgRxInventory - Inventory] Set Offline with no message', props()),
    downloadCostReport: createAction('[NgRxInventory - Inventory] Download inventory cost report with options from modal', props()),
    downloadCostComparisonReport: createAction('[NgRxInventory - Inventory] Download inventory cost comparison report with options from modal', props()),
    downloadInventory: createAction('[NgRxInventory - Inventory] Download inventory with options from modal', props()),
    downloadExtendedCostReport: createAction('[NgRxInventory - Inventory] Download inventory Extended Cost Report', props()),
    downloadExtendedCostReportSuccess: createAction('[NGRXTrace - Inventory] Download inventory Extended Cost Report Success', props()),
    downloadExtendedCostReportFail: createAction('[NGRXTrace - Inventory] Download inventory Extended Cost Report Failure', props()),
    loadCustomerMigration: createAction('[NgRxInventory - Inventory] Load inventory migration data for customer'),
    loadCustomerMigrationSuccess: createAction('[NgRxInventory - Inventory] Load inventory migration data for customer Success', props()),
    loadCustomerMigrationFail: createAction('[NgRxInventory - Inventory] Load inventory migration data for customer Failure', props()),
    initiateMigration: createAction('[NgRxInventory - Inventory] Post to migration API to start inventory migration on backend', props()),
    migrationSuccess: createAction('[NgRxInventory - Inventory] Inventory migration on backend succeeded'),
    migrationFail: createAction('[NgRxInventory - Inventory] Inventory migration process failed', props()),
    updateInventorySortModal: createAction('[NgRxInventory - Inventory] Update open/close state for Inventory Mobile Sort Modal', props()),
};

/* eslint-disable no-shadow */
var InventoryEndpoints;
(function (InventoryEndpoints) {
    InventoryEndpoints["uiConstants"] = "uiConstants";
    InventoryEndpoints["inventory"] = "inventory";
    InventoryEndpoints["extendedCostReport"] = "extendedCostReport";
    InventoryEndpoints["customerMigration"] = "customerMigration";
    InventoryEndpoints["inventoryMigration"] = "inventoryMigration";
    InventoryEndpoints["updateItemValues"] = "inventory/updateInventoryItemValues";
    InventoryEndpoints["allDepartments"] = "inventory/byCustomer";
    InventoryEndpoints["foodCostReport"] = "foodCostReport";
    InventoryEndpoints["foodPurchaseTotal"] = "foodCostReport/USFPurchaseTotal";
    InventoryEndpoints["updateItems"] = "inventory/updateItems";
})(InventoryEndpoints || (InventoryEndpoints = {}));

const INVENTORY_GROUP_ACTIONS = {
    create: createAction('[NgRxInventory - InventoryGroup] Create Inventory Group from Domain API', props()),
    createSuccess: createAction('[NgRxInventory - InventoryGroup] Create Inventory Group from Domain API succeeded', props()),
    createFail: createAction('[NgRxInventory - InventoryGroup] Create Inventory Group from Domain API failed', props()),
    updateSelectedGroup: createAction('[NgRxInventory - Inventory] Update Inventory selected group', props()),
    delete: createAction('[NgRxInventory - InventoryGroup] Delete Inventory Group from Domain API', props()),
    deleteAndMoveItems: createAction('[NgRxInventory - InventoryGroup] Delete Inventory Group and move items to unassigned from Domain API', props()),
    move: createAction('[NgRxInventory - InventoryGroup] Move Inventory Group in Domain API', props()),
    update: createAction('[NgRxInventory - InventoryGroup] Update Inventory Group Name in Domain API', props()),
};

const INVENTORY_ITEM_ACTIONS = {
    create: createAction('[NgRxInventory - InventoryItem] Create Inventory Item from Domain API', props()),
    createSuccess: createAction('[NgRxInventory - InventoryItem] Create Inventory Item from Domain API succeeded', props()),
    createFail: createAction('[NgRxInventory - InventoryItem] Create Inventory Item from Domain API failed', props()),
    update: createAction('[NgRxInventory - InventoryItem] Update Inventory Item from Domain API', props()),
    selectInventoryItem: createAction('[NGRXInventory - InventoryItem] Update Inventory Item selection', props()),
    resetInventoryItemSelections: createAction('[NGRXInventory - InventoryItem] Reset Inventory Item selection', props()),
    updateUOM: createAction('[NGRXInventory - InventoryItem] Update Inventory Item UOM', props()),
    resetUOM: createAction('[NGRXInventory - InventoryItem] Reset Inventory Item UOM', props()),
    updateQuantity: createAction('[NgRxInventory - InventoryItem] Update Inventory Item quantity', props()),
    updateQuantityFail: createAction('[NgRxInventory - InventoryItem] Update Inventory Item quantity Failed', props()),
    updateQuantitySuccess: createAction('[NgRxInventory - InventoryItem] Update Inventory Item quantity Succeeseedd', props()),
    updatePriceOverride: createAction('[NGRXTrace - InventoryItem] Update Inventory Item Price Override', props()),
    updateInventoryItemModalOptions: createAction('[NGRXInventory - InventoryItem] - Update inventory item modal options', props()),
    saveInventoryItemModalOptions: createAction('[NGRXInventory - InventoryItem] - Save inventory item modal options actual inventory item', props()),
    updateConversionFactor: createAction('[NgRxInventory - InventoryItem] Update Inventory Item Conversion Factor', props()),
    updateUOMAndConversionFactor: createAction('[NgRxInventory - InventoryItem] Update Inventory Items UOM and Conversion Factor', props()),
    moveInventoryItemsIntoGroup: createAction('[NGRXInventory - InventoryItem] - Move inventory items into group', props()),
    moveInventoryItemByLineNumber: createAction('[NGRXInventory - InventoryItem] - Move inventory item by line number', props()),
    moveInventoryItemDuplicateFailure: createAction('[NGRXInventory - InventoryItem] - Move inventory item by line number duplicate failure'),
    selectAllInventoryItems: createAction('[NGRXInventory - InventoryItem] - Select all Inventory Items by InventoryId or by Group', props()),
    copyInventoryItemsIntoGroup: createAction('[NGRXInventory - InventoryItem] - Copy inventory items into group', props()),
    addInventoryItemsIntoGroup: createAction('[NGRXInventory - InventoryItem] - Add inventory items into group', props()),
    deleteInventoryItemsInGroup: createAction('[NGRXTrace - InventoryItem] - Delete Items in a group', props()),
    updateCustomConversionFactorUnit: createAction('[NgRxInventory - InventoryItem] Update Inventory Custom Conversion Factor Unit', props()),
    nonUSFValueUpdate: createAction('[NgRxInventory - InventoryItem] Update Inventory Items That Match Product Number', props()),
    nonUSFValueUpdateSuccess: createAction('[NgRxInventory - InventoryItem] Update Inventory Items That Match Product Number Successeded', props()),
    nonUSFValueUpdateFail: createAction('[NgRxInventory - InventoryItem] Update Inventory Items That Match Product Number Failed', props()),
    bulkItemUpdate: createAction('[Inventory Item] Bulk Update V2', props()),
    setBulkSyncing: createAction('[Inventory Item] Set Bulk Syncing', props()),
    bulkItemUpdateSuccess: createAction('[Inventory Item] Bulk Update V2 Success', props()),
    bulkItemUpdateFail: createAction('[Inventory Item] Bulk Update V2 Fail', props()),
    incrementRetryCount: createAction('[Inventory Item] Increment Retry Count', props()),
    revertFailedUpdates: createAction('[Inventory Item] Revert Failed Updates', props()),
    clearFailedPendingUpdates: createAction('[Inventory Item] Clear Failed Pending Updates', props()),
    resetItemsWithLatestProductData: createAction('[NgRxInventory - InventoryItem] Reset Inventory Item (s) with Latest Product Data', props()),
    checkPendingUpdates: createAction('[NgRxInventory - InventoryItem] Check For Pending Updates'),
    processPendingItemUpdates: createAction('[NgRxInventory - InventoryItem] Process Pending Item Updates', props()),
    clearMissedItemUpdatesAfterTimeout: createAction('[Inventory Item] Clear Missed Item Updates After Timeout', props()),
};

const INVENTORY_WORKSHEET_MODE_DEFAULT = 'count';
const IN_EDIT_NAME_MODE_DEFAULT = false;
const IN_EDIT_GROUPS_MODE_DEFAULT = false;
const SELECTED_GROUP_DEFAULT = {
    groupName: 'All',
};
const SELECTED_PRICE_DEFAULT = 'LP';
const NAME_EXISTS_DEFAULT = false;
const SHOW_COMPLETED_DEFAULT = false;
const BASE_INVENTORY_UNITS = [
    'EA',
    'LB',
    'OZ',
    'G',
    'GA',
    'BATCH',
    'PIECE',
    'CS',
];
const NAME_VALIDATOR_DEFAULT = '';
const DATE_VALIDATOR_DEFAULT = '';

var GroupPosition;
(function (GroupPosition) {
    GroupPosition["top"] = "Move to Top of Group";
    GroupPosition["bottom"] = "Move to Bottom of Group";
})(GroupPosition || (GroupPosition = {}));
var InventoryItemEditOptions;
(function (InventoryItemEditOptions) {
    InventoryItemEditOptions["chooseGroup"] = "Choose Group";
    InventoryItemEditOptions["choosePosition"] = "Choose Position";
    InventoryItemEditOptions["inventoryUnit"] = "Inventory Unit";
    InventoryItemEditOptions["price"] = "Price";
})(InventoryItemEditOptions || (InventoryItemEditOptions = {}));

const initialInventoryItemModalOptions = {
    isOpen: false,
    isPDPOverlayOpen: false,
    imageThumbnail: '',
    brand: '',
    description: '',
    productNumber: 0,
    manufacturerNumber: '',
    numberOfUnits: 0,
    unitPrice: 0,
    numberOfCases: 0,
    casePrice: 0,
    value: 0,
    selectedGroup: {},
    selectedPosition: '',
    groups: [],
    itemSequenceNumber: 0,
    unit: '',
    customUnit: '',
    priceOverrideInd: '',
    overrideCasePrice: 0,
    salesPackSize: '',
    selectedOption: InventoryItemEditOptions.chooseGroup,
    invConvFactor: 0,
    inventoryId: '',
    inventoryItemId: 0,
    itemIndex: 0,
    groupIndex: 0,
    initialGroup: {},
    initialUnit: '',
    initialInvConvFactor: 0,
    initialPriceOverrideInd: '',
    initialOverrideCasePrice: 0,
    initialCustomUnit: '',
    saveButtonIsDisabled: false,
    catchWeightFlag: false,
    marketBreakFlag: false,
    customErrorFlag: false,
    nonUSFFlag: false,
};

const initialCustomerMigrationStatus = {
    customerNumber: 0,
    divisionNumber: 0,
    customerMigrated: '',
    hasR2Inventory: false,
    updateUserId: 0,
    updateDtm: '12345',
};

var SortDirections;
(function (SortDirections) {
    SortDirections["asc"] = "asc";
    SortDirections["desc"] = "desc";
})(SortDirections || (SortDirections = {}));
var InventoriesSortOptions;
(function (InventoriesSortOptions) {
    InventoriesSortOptions["name"] = "inventoryName";
    InventoriesSortOptions["lastUpdated"] = "ecomUpdateDtm";
    InventoriesSortOptions["created"] = "ecomCreatedDtm";
    InventoriesSortOptions["numProductsByQuantities"] = "numProductsByQuantities";
    InventoriesSortOptions["totalValue"] = "totalPrice";
})(InventoriesSortOptions || (InventoriesSortOptions = {}));
/* INVENTORY LANDING (RECENT INVENTORIES) */
// sortDirection values are opposite of the header's default sort direction
// EXCEPT for default header (lastUpdated)
const recentInventoriesHeaders = {
    [InventoriesSortOptions.name]: {
        sortDirection: SortDirections.desc,
        displayText: 'Name',
    },
    [InventoriesSortOptions.lastUpdated]: {
        sortDirection: SortDirections.desc,
        displayText: 'Last Updated',
    },
    [InventoriesSortOptions.created]: {
        sortDirection: SortDirections.asc,
        displayText: 'Created',
    },
    [InventoriesSortOptions.numProductsByQuantities]: {
        sortDirection: SortDirections.asc,
        displayText: 'Products',
    },
    [InventoriesSortOptions.totalValue]: {
        sortDirection: SortDirections.asc,
        displayText: 'Value',
    },
};
const initialRecentInventoriesSort = {
    inventories: {
        selectedHeader: InventoriesSortOptions.lastUpdated,
        headers: recentInventoriesHeaders,
    },
};
/* ALL INVENTORIES */
// sortDirection values are opposite header's default sort direction
// EXCEPT for default header (created)
const allInventoriesHeaders = {
    [InventoriesSortOptions.name]: {
        sortDirection: SortDirections.desc,
        displayText: 'Name',
    },
    [InventoriesSortOptions.lastUpdated]: {
        sortDirection: SortDirections.asc,
        displayText: 'Last Updated',
    },
    [InventoriesSortOptions.created]: {
        sortDirection: SortDirections.desc,
        displayText: 'Created',
    },
    [InventoriesSortOptions.numProductsByQuantities]: {
        sortDirection: SortDirections.asc,
        displayText: 'Products',
    },
    [InventoriesSortOptions.totalValue]: {
        sortDirection: SortDirections.asc,
        displayText: 'Value',
    },
};
const initialAllInventoriesSort = {
    inventories: {
        selectedHeader: InventoriesSortOptions.created,
        headers: allInventoriesHeaders,
    },
};
/* INVENTORY WORKSHEET COUNT MODE */
var InventoryWorksheetCountSortOptions;
(function (InventoryWorksheetCountSortOptions) {
    InventoryWorksheetCountSortOptions["lineNum"] = "itemSequenceNumber";
    InventoryWorksheetCountSortOptions["productName"] = "productName";
    InventoryWorksheetCountSortOptions["byCase"] = "caseOnHand";
    InventoryWorksheetCountSortOptions["byUnit"] = "eachOnHand";
    InventoryWorksheetCountSortOptions["total"] = "total";
    InventoryWorksheetCountSortOptions["value"] = "value";
})(InventoryWorksheetCountSortOptions || (InventoryWorksheetCountSortOptions = {}));
// sortDirection values are opposite of header's default sort direction
// EXCEPT for default header (lineNum)
const inventoryWorksheetCountHeaders = {
    [InventoryWorksheetCountSortOptions.lineNum]: {
        sortDirection: SortDirections.asc,
        displayText: 'Line',
    },
    [InventoryWorksheetCountSortOptions.productName]: {
        sortDirection: SortDirections.desc,
        displayText: 'Product Name',
    },
    [InventoryWorksheetCountSortOptions.byCase]: {
        sortDirection: SortDirections.desc,
        displayText: 'By Case',
    },
    [InventoryWorksheetCountSortOptions.byUnit]: {
        sortDirection: SortDirections.desc,
        displayText: 'By Unit',
    },
    [InventoryWorksheetCountSortOptions.total]: {
        sortDirection: SortDirections.desc,
        displayText: 'Total',
    },
    [InventoryWorksheetCountSortOptions.value]: {
        sortDirection: SortDirections.desc,
        displayText: 'Value',
    },
};
// INVENTORY WORKSHEET EDIT MODE
var InventoryWorksheetEditSortOptions;
(function (InventoryWorksheetEditSortOptions) {
    InventoryWorksheetEditSortOptions["lineNum"] = "itemSequenceNumber";
    InventoryWorksheetEditSortOptions["productName"] = "productName";
    InventoryWorksheetEditSortOptions["group"] = "groupName";
    InventoryWorksheetEditSortOptions["unitDescription"] = "uom";
    InventoryWorksheetEditSortOptions["unitByCase"] = "displayConvFactor";
    InventoryWorksheetEditSortOptions["casePrice"] = "casePrice";
    InventoryWorksheetEditSortOptions["unitPrice"] = "unitPrice";
})(InventoryWorksheetEditSortOptions || (InventoryWorksheetEditSortOptions = {}));
// sortDirection values are opposite of header's default sort direction
// EXCEPT for default header (lineNum)
const inventoryWorksheetEditHeaders = {
    [InventoryWorksheetEditSortOptions.lineNum]: {
        sortDirection: SortDirections.asc,
        displayText: 'Line',
    },
    [InventoryWorksheetEditSortOptions.productName]: {
        sortDirection: SortDirections.desc,
        displayText: 'Product Name',
    },
    [InventoryWorksheetEditSortOptions.group]: {
        sortDirection: SortDirections.desc,
        displayText: 'Group',
    },
    [InventoryWorksheetEditSortOptions.unitDescription]: {
        sortDirection: SortDirections.desc,
        displayText: 'Unit Description',
    },
    [InventoryWorksheetEditSortOptions.unitByCase]: {
        sortDirection: SortDirections.desc,
        displayText: 'Unit By Case',
    },
    [InventoryWorksheetEditSortOptions.casePrice]: {
        sortDirection: SortDirections.desc,
        displayText: 'Price',
    },
    [InventoryWorksheetEditSortOptions.unitPrice]: {
        sortDirection: SortDirections.desc,
        displayText: 'Unit Price',
    },
};

const PENDING_UPDATE_KEY_DELIMITER = '|';
const generatePendingItemUpdateKey = (key) => `${key.inventoryId}${PENDING_UPDATE_KEY_DELIMITER}${key.productNumber}${PENDING_UPDATE_KEY_DELIMITER}${key.groupName}`;
const parsePendingItemUpdateKey = (key) => {
    const [inventoryId, productNumber, groupName] = key.split(PENDING_UPDATE_KEY_DELIMITER);
    return {
        inventoryId,
        productNumber: Number(productNumber),
        groupName,
    };
};
const createInventoryItemLookup = (inventory) => {
    const lookup = new Map();
    inventory.groups?.forEach((group) => {
        group.groupItems?.forEach((item) => {
            const key = generatePendingItemUpdateKey({
                inventoryId: inventory.inventoryId,
                productNumber: item.itemNumber,
                groupName: group.groupName,
            });
            lookup.set(key, { item, group });
        });
    });
    return lookup;
};
const mergeInventoryStateWithAPIInformation = (inventoryFromState, inventoryFromApi, pendingItemUpdates) => {
    const updatedInventory = generateMutableInventory(inventoryFromApi);
    // Preserve non-API related state properties
    updatedInventory.isSelected = inventoryFromState.isSelected;
    updatedInventory.inventoryWorksheetCountSort =
        inventoryFromState.inventoryWorksheetCountSort;
    updatedInventory.inventoryWorksheetEditSort =
        inventoryFromState.inventoryWorksheetEditSort;
    // Create a map of state groups for efficient lookup
    const stateGroupMap = new Map(inventoryFromState.groups?.map((group) => [group.groupName, group]) || []);
    // Create a map of pending updates for efficient lookup
    const pendingUpdateLookup = new Map();
    Object.entries(pendingItemUpdates).forEach(([key, state]) => {
        if (!!state && state?.pendingItemUpdates?.length > 0) {
            pendingUpdateLookup.set(key, state);
        }
    });
    updatedInventory.groups = updatedInventory.groups?.map((apiGroup) => {
        const stateGroup = stateGroupMap.get(apiGroup.groupName);
        if (!stateGroup) {
            return apiGroup;
        }
        apiGroup.groupItems = apiGroup.groupItems?.map((apiItem) => {
            const key = generatePendingItemUpdateKey({
                inventoryId: inventoryFromApi.inventoryId,
                productNumber: apiItem.itemNumber,
                groupName: apiGroup.groupName,
            });
            const pendingState = pendingUpdateLookup.get(key);
            if (!pendingState) {
                return apiItem;
            }
            // Apply any remaining pending updates
            return pendingState.pendingItemUpdates.reduce((item, pendingUpdate) => ({
                ...item,
                ...pendingUpdate.itemData,
            }), apiItem);
        });
        return apiGroup;
    });
    return updatedInventory;
};
const updatePendingItemState = (state, inventoryId, updates, itemLookup) => {
    const updatedPendingUpdates = { ...state.pendingItemUpdates };
    updates.forEach((update) => {
        const key = generatePendingItemUpdateKey({
            inventoryId,
            productNumber: update.itemNumber,
            groupName: update.groupName,
        });
        const currentItem = itemLookup.get(key)?.item;
        if (!currentItem) {
            return;
        }
        const originalValues = extractOriginalValues(currentItem, update.itemData);
        const filteredItemData = filterUndefinedFields(update.itemData);
        const existingState = updatedPendingUpdates[key] || {
            pendingItemUpdates: [],
            isSyncing: false,
        };
        updatedPendingUpdates[key] = {
            ...existingState,
            pendingItemUpdates: [
                ...existingState.pendingItemUpdates,
                {
                    itemData: filteredItemData,
                    originalValues,
                    retryCount: 0,
                },
            ],
        };
    });
    return updatedPendingUpdates;
};
const extractOriginalValues = (currentItem, itemData) => Object.keys(itemData).reduce((acc, field) => ({
    ...acc,
    [field]: currentItem[field],
}), {});
const filterUndefinedFields = (itemData) => Object.entries(itemData)
    .filter(([_, value]) => value !== undefined)
    .reduce((acc, [key, value]) => ({
    ...acc,
    [key]: value,
}), {});
const updateInventoryGroups = (inventory, inventoryId, updates) => {
    const updatesByKey = new Map(updates.map((update) => [
        generatePendingItemUpdateKey({
            inventoryId,
            productNumber: update.itemNumber,
            groupName: update.groupName,
        }),
        update,
    ]));
    return inventory.groups?.map((group) => ({
        ...group,
        groupItems: group.groupItems?.map((item) => {
            const key = generatePendingItemUpdateKey({
                inventoryId,
                productNumber: item.itemNumber,
                groupName: group.groupName,
            });
            const update = updatesByKey.get(key);
            return update ? { ...item, ...update.itemData } : item;
        }),
    }));
};
const processMissedItems = (pendingUpdates, missedItems, inventoryId) => {
    const processedItems = new Set();
    missedItems?.forEach((missedItem) => {
        const key = generatePendingItemUpdateKey({
            inventoryId,
            productNumber: Number(missedItem.productNumber),
            groupName: missedItem.groupName,
        });
        delete pendingUpdates[key];
        processedItems.add(key);
    });
    return processedItems;
};
const processRemainingUpdates = (pendingUpdates, apiItemLookup, inventoryId, processedItems) => {
    Object.entries(pendingUpdates).forEach(([key, pendingState]) => {
        const { inventoryId: itemInventoryId } = parsePendingItemUpdateKey(key);
        if (itemInventoryId !== inventoryId || processedItems.has(key)) {
            return;
        }
        processedItems.add(key);
        const apiItem = apiItemLookup.get(key);
        if (!apiItem?.item) {
            delete pendingUpdates[key];
            return;
        }
        processRemainingUpdate(pendingUpdates, key, pendingState, apiItem);
    });
};
const processRemainingUpdate = (pendingUpdates, key, pendingState, apiItem) => {
    if (!pendingState?.pendingItemUpdates?.length) {
        delete pendingUpdates[key];
        return;
    }
    // Find the last update that matches the API state by reversing the array first
    const reversedIndex = [...pendingState.pendingItemUpdates]
        .reverse()
        .findIndex((update) => Object.entries(update.itemData).every(([field, value]) => apiItem.item[field] === value));
    // Convert the reversed index back to the original array index
    const matchingIndex = reversedIndex === -1
        ? -1
        : pendingState.pendingItemUpdates.length - 1 - reversedIndex;
    // If we found a matching update, remove it and all previous updates
    if (matchingIndex !== -1) {
        const remainingUpdates = pendingState.pendingItemUpdates.slice(matchingIndex + 1);
        if (remainingUpdates.length === 0) {
            delete pendingUpdates[key];
        }
        else {
            pendingUpdates[key] = {
                pendingItemUpdates: remainingUpdates,
                isSyncing: false,
            };
        }
    }
};
const getOriginalValuesForFailedUpdates = (pendingUpdates, failedUpdates) => {
    const originalValues = new Map();
    failedUpdates.forEach((update) => {
        const key = generatePendingItemUpdateKey(update);
        const pendingState = pendingUpdates[key];
        if (!!pendingState?.pendingItemUpdates &&
            pendingState.pendingItemUpdates.length > 0) {
            // Combine all original values, starting from the most recent update
            // and working backwards to get the original state before any updates
            const combinedOriginalValues = pendingState.pendingItemUpdates
                .slice()
                .reverse()
                .reduce((combined, pendingUpdate) => ({
                ...combined,
                ...pendingUpdate.originalValues,
            }), {});
            originalValues.set(key, combinedOriginalValues);
        }
    });
    return originalValues;
};
const revertInventoryGroups = (inventory, inventoryId, originalValues) => inventory.groups?.map((group) => ({
    ...group,
    groupItems: group.groupItems?.map((item) => {
        const key = generatePendingItemUpdateKey({
            inventoryId,
            productNumber: item.itemNumber,
            groupName: group.groupName,
        });
        const originalValue = originalValues.get(key);
        if (originalValue) {
            return {
                ...item,
                ...originalValue, // Revert to original values
            };
        }
        return item;
    }),
}));
const clearMissedItemUpdates = (missedItemUpdates, updates, inventoryId) => {
    if (!Object.keys(missedItemUpdates).length || !updates.length) {
        return missedItemUpdates;
    }
    const keysToDelete = new Set(updates.map((update) => generatePendingItemUpdateKey({
        inventoryId,
        productNumber: update.itemNumber,
        groupName: update.groupName,
    })));
    if (keysToDelete.size === 0) {
        return missedItemUpdates;
    }
    const result = Object.create(null);
    for (const [key, value] of Object.entries(missedItemUpdates)) {
        if (!keysToDelete.has(key)) {
            result[key] = value;
        }
    }
    return result;
};
const updateMissedItemUpdates = (pendingUpdates, apiItemLookup, stateItemLookup, missedItems, existingMissedUpdates) => {
    if (!missedItems?.length) {
        return existingMissedUpdates;
    }
    const updatedMissedItemUpdates = {
        ...existingMissedUpdates,
    };
    const relevantFields = new Set([
        'convFactorUnit',
        'customConvFactorUnit',
        'invConvFactor',
        'caseOnHand',
        'eachOnHand',
        'overrideCasePrice',
        'priceOverrideInd',
        'productCasePrice',
        'productUnitPrice',
        'productUnitOfMeasure',
        'productConvFactor',
    ]);
    for (const missedItem of missedItems) {
        const key = generatePendingItemUpdateKey(missedItem);
        const apiItem = apiItemLookup.get(key)?.item;
        const stateItem = stateItemLookup.get(key)?.item;
        const pendingState = pendingUpdates[key];
        if (!apiItem || !stateItem || !pendingState) {
            continue;
        }
        const missedFields = detectUpdatedFields(apiItem, stateItem, pendingState, relevantFields);
        if (Object.keys(missedFields).length > 0) {
            updatedMissedItemUpdates[key] = { ...missedFields };
        }
        else {
            delete updatedMissedItemUpdates[key];
        }
    }
    return updatedMissedItemUpdates;
};
const detectUpdatedFields = (apiItem, stateItem, pendingState, relevantFields) => {
    const missedFields = Object.create(null);
    const originalValuesOfItem = getOriginalValuesOfItem(pendingState);
    for (const field of relevantFields) {
        const apiValue = apiItem[field];
        if (Object.prototype.hasOwnProperty.call(originalValuesOfItem, field)) {
            const originalValue = originalValuesOfItem[field];
            if (apiValue !== originalValue) {
                missedFields[field] = true;
            }
        }
        else {
            const stateValue = stateItem[field];
            if (apiValue !== stateValue) {
                missedFields[field] = true;
            }
        }
    }
    return missedFields;
};
const getOriginalValuesOfItem = (pendingState) => {
    const result = Object.create(null);
    for (let i = pendingState.pendingItemUpdates.length - 1; i >= 0; i--) {
        const update = pendingState.pendingItemUpdates[i];
        Object.assign(result, update.originalValues);
    }
    return result;
};

/* eslint-disable max-len */
const inventoryAdapter = createEntityAdapter({
    selectId: (inventory) => inventory?.inventoryId,
});
const initialInventoryState = inventoryAdapter.getInitialState({
    error: undefined,
    loaded: false,
    isOnline: true,
    inventoryOptions: {
        inventoryWorksheetMode: INVENTORY_WORKSHEET_MODE_DEFAULT,
        inEditNameMode: IN_EDIT_NAME_MODE_DEFAULT,
        inEditGroupsMode: IN_EDIT_GROUPS_MODE_DEFAULT,
        selectedGroup: SELECTED_GROUP_DEFAULT,
        priceSystemSelected: SELECTED_PRICE_DEFAULT,
        validators: {
            inventoryName: NAME_VALIDATOR_DEFAULT,
            inventoryDate: DATE_VALIDATOR_DEFAULT,
        },
        searchKey: '',
        selections: [],
        showCompleted: SHOW_COMPLETED_DEFAULT,
        productsAdded: undefined,
        isInfoModalOpen: false,
        inventorySnapshot: undefined,
        isCustomUOMOpen: false,
        isSortModalOpen: false,
        lastUpdatedByUser: {},
        missedItems: [],
    },
    recentInventoriesSort: initialRecentInventoriesSort,
    allInventoriesSearchKey: '',
    allInventoriesSort: initialAllInventoriesSort,
    inventoryItemModalOptions: initialInventoryItemModalOptions,
    customerMigrationStatus: initialCustomerMigrationStatus,
    pendingItemUpdates: {},
    missedItemUpdates: {},
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _inventoryReducer = createReducer(initialInventoryState, 
/* INVENTORY */
on(INVENTORY_ACTIONS.resetStore, (state) => initialInventoryState), on(INVENTORY_ACTIONS.load, (state) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(INVENTORY_ACTIONS.loadWithAllDepartments, (state) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(INVENTORY_ACTIONS.loadSuccess, (state, action) => {
    const inventoriesState = action.inventories.map((inventory) => setInventoryWorksheetSorts(inventory));
    return inventoryAdapter.upsertMany(inventoriesState, {
        ...state,
        loaded: true,
        loading: false,
    });
}), on(INVENTORY_ACTIONS.stripAdditionalDepartments, (state, action) => {
    const check = state.entities;
    const idsToRemove = [];
    state.ids.forEach((id) => {
        if (check[id]?.departmentNumber !== action.departmentNumber) {
            idsToRemove.push(id);
        }
    });
    return inventoryAdapter.removeMany(idsToRemove, state);
}), on(INVENTORY_ACTIONS.loadFail, (state, action) => ({
    ...state,
    error: action.error,
    loaded: true,
    loading: false,
})), on(INVENTORY_ACTIONS.setOnlineFlagWithoutMessage, (state, action) => ({
    ...state,
    isOnline: action.online,
})), on(INVENTORY_ACTIONS.enterOffline, (state) => ({
    ...state,
    isOnline: false,
})), on(INVENTORY_ACTIONS.exitOffline, (state) => ({ ...state, isOnline: true })), on(INVENTORY_ACTIONS.create, (state) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(INVENTORY_ACTIONS.createSuccess, (state, action) => {
    const updatedInventory = setInventoryWorksheetSorts(action.inventory);
    state = inventoryAdapter.addOne(updatedInventory, state);
    return {
        ...state,
        loaded: true,
        loading: false,
    };
}), on(INVENTORY_ACTIONS.createFail, INVENTORY_GROUP_ACTIONS.createFail, (state, action) => ({
    ...state,
    error: action.error,
    loaded: true,
    loading: false,
})), on(INVENTORY_ACTIONS.update, (state, action) => {
    const inventoryForAction = generateMutableInventory(action.inventory);
    return inventoryAdapter.upsertOne(inventoryForAction, state);
}), on(INVENTORY_ACTIONS.completeSuccess, INVENTORY_ACTIONS.updateSuccess, INVENTORY_ACTIONS.updateWithToastMessageSuccess, INVENTORY_ACTIONS.updateWithTracingSuccess, INVENTORY_GROUP_ACTIONS.createSuccess, INVENTORY_ITEM_ACTIONS.updateQuantitySuccess, (state, action) => {
    // Pulling latest state because we only want to update specefic values from api.
    const inventory = state?.entities[action.inventory.inventoryId];
    if (!inventory) {
        return { ...state };
    }
    const apiUpdatedInventory = updateInventoryStateWithAPIInformation(inventory, action.inventory);
    return inventoryAdapter.upsertOne(apiUpdatedInventory, state);
}), on(INVENTORY_ACTIONS.updateRecentInventoriesSort, (state, action) => {
    // On click of new header, update selected header's sort direction and reset others' sort directions to their initial values
    // EXCEPT for default header
    const updatedHeaders = updateInventorySortHeaders(state.recentInventoriesSort, 'inventories', action.selectedHeader, InventoriesSortOptions.lastUpdated, recentInventoriesHeaders, action.sortDirection);
    return {
        ...state,
        ['recentInventoriesSort']: {
            ...state.recentInventoriesSort,
            inventories: {
                selectedHeader: action.selectedHeader,
                headers: updatedHeaders,
            },
        },
    };
}), on(INVENTORY_ACTIONS.resetRecentInventoriesSort, (state) => ({
    ...state,
    recentInventoriesSort: initialRecentInventoriesSort,
})), on(INVENTORY_ACTIONS.updateAllInventoriesSort, (state, action) => {
    // On click of new header, update selected header's sort direction and reset others' sort directions to their initial values
    // EXCEPT for default header
    const updatedHeaders = updateInventorySortHeaders(state.allInventoriesSort, 'inventories', action.selectedHeader, InventoriesSortOptions.created, allInventoriesHeaders, action.sortDirection);
    return {
        ...state,
        ['allInventoriesSort']: {
            ...state.allInventoriesSort,
            inventories: {
                selectedHeader: action.selectedHeader,
                headers: updatedHeaders,
            },
        },
    };
}), on(INVENTORY_ACTIONS.resetAllInventoriesSort, (state) => ({
    ...state,
    allInventoriesSort: initialAllInventoriesSort,
})), on(INVENTORY_ACTIONS.updateInventoryWorksheetMode, (state, action) => ({
    ...state,
    inventoryOptions: {
        ...state.inventoryOptions,
        inventoryWorksheetMode: action.inventoryWorksheetMode,
    },
})), on(INVENTORY_ACTIONS.updateInventoryWorksheetCountSort, (state, action) => {
    let inventory = state?.entities[action.inventoryId];
    if (!inventory) {
        return { ...state };
    }
    // Inventory Worksheet (Count Mode)
    // On click of new header, update selected header's sort direction and reset others' sort directions to their initial values
    // EXCEPT for default header
    // If section is 'All', then update headers for all groups - this comes from mobile sort modal
    if (action.inventorySection === 'All') {
        const updatedInventorySort = updateAllWorksheetSortHeaders(inventory.inventoryWorksheetCountSort, action.selectedHeader, action.sortDirection);
        inventory = {
            ...inventory,
            inventoryWorksheetCountSort: updatedInventorySort,
        };
    }
    else {
        const updatedHeaders = updateInventorySortHeaders(inventory.inventoryWorksheetCountSort, action.inventorySection, action.selectedHeader, InventoryWorksheetCountSortOptions.lineNum, inventoryWorksheetCountHeaders, action.sortDirection);
        inventory = {
            ...inventory,
            inventoryWorksheetCountSort: {
                ...inventory.inventoryWorksheetCountSort,
                [action.inventorySection]: {
                    selectedHeader: action.selectedHeader,
                    headers: updatedHeaders,
                },
            },
        };
    }
    return inventoryAdapter.upsertOne(inventory, state);
}), on(INVENTORY_ACTIONS.resetInventoryWorksheetCountSort, (state, action) => {
    let inventory = state?.entities[action.inventoryId];
    if (!inventory) {
        return { ...state };
    }
    let initialInventoryWorksheetCountSort = {};
    inventory?.groups?.forEach((group) => {
        initialInventoryWorksheetCountSort = setInventoryWorksheetCountSort(initialInventoryWorksheetCountSort, group?.groupName);
    });
    inventory = {
        ...inventory,
        inventoryWorksheetCountSort: initialInventoryWorksheetCountSort,
    };
    return inventoryAdapter.upsertOne(inventory, state);
}), on(INVENTORY_ACTIONS.updateInventoryWorksheetEditSort, (state, action) => {
    let inventory = state?.entities[action.inventoryId];
    if (!inventory) {
        return { ...state };
    }
    // Inventory Worksheet (Edit Mode)
    // On click of new header, update selected header's sort direction and reset others' sort directions to their initial values
    // EXCEPT for default header
    // If section is 'All', then update headers for all groups - this comes from mobile sort modal
    if (action.inventorySection === 'All') {
        const updatedInventorySort = updateAllWorksheetSortHeaders(inventory.inventoryWorksheetEditSort, action.selectedHeader, action.sortDirection);
        inventory = {
            ...inventory,
            inventoryWorksheetEditSort: updatedInventorySort,
        };
    }
    else {
        const updatedHeaders = updateInventorySortHeaders(inventory.inventoryWorksheetEditSort, action.inventorySection, action.selectedHeader, InventoryWorksheetEditSortOptions.lineNum, inventoryWorksheetEditHeaders, action.sortDirection);
        inventory = {
            ...inventory,
            inventoryWorksheetEditSort: {
                ...inventory.inventoryWorksheetEditSort,
                [action.inventorySection]: {
                    selectedHeader: action.selectedHeader,
                    headers: updatedHeaders,
                },
            },
        };
    }
    return inventoryAdapter.upsertOne(inventory, state);
}), on(INVENTORY_ACTIONS.resetInventoryWorksheetEditSort, (state, action) => {
    let inventory = state?.entities[action.inventoryId];
    if (!inventory) {
        return { ...state };
    }
    let initialInventoryWorksheetEditSort = {};
    inventory?.groups?.forEach((group) => {
        initialInventoryWorksheetEditSort = setInventoryWorksheetEditSort(initialInventoryWorksheetEditSort, group?.groupName);
    });
    inventory = {
        ...inventory,
        inventoryWorksheetEditSort: initialInventoryWorksheetEditSort,
    };
    return inventoryAdapter.upsertOne(inventory, state);
}), on(INVENTORY_GROUP_ACTIONS.updateSelectedGroup, (state, action) => ({
    ...state,
    inventoryOptions: {
        ...state.inventoryOptions,
        selectedGroup: action.selectedGroup,
    },
})), on(INVENTORY_ACTIONS.updateIsSelected, (state, action) => {
    const updatedInventories = [];
    let updatedInventory;
    state.ids.forEach((id) => {
        if (id === action.inventoryId) {
            updatedInventory = {
                ...state.entities[id],
                isSelected: action.isSelected,
            };
        }
        else {
            updatedInventory = {
                ...state.entities[id],
                isSelected: false,
            };
        }
        updatedInventories.push(updatedInventory);
    });
    return inventoryAdapter.upsertMany(updatedInventories, state);
}), on(INVENTORY_ACTIONS.updateSelectedPrice, (state, action) => ({
    ...state,
    inventoryOptions: {
        ...state.inventoryOptions,
        priceSystemSelected: action.selectedPrice,
    },
})), on(INVENTORY_ACTIONS.updateInEditNameMode, (state, action) => ({
    ...state,
    inventoryOptions: {
        ...state.inventoryOptions,
        inEditNameMode: action.inEditNameMode,
    },
})), on(INVENTORY_ACTIONS.updateInEditGroupsMode, (state, action) => ({
    ...state,
    inventoryOptions: {
        ...state.inventoryOptions,
        inEditGroupsMode: action.inEditGroupsMode,
    },
})), on(INVENTORY_ACTIONS.setNameValidator, (state, action) => ({
    ...state,
    inventoryOptions: {
        ...state.inventoryOptions,
        validators: {
            ...state.inventoryOptions.validators,
            inventoryName: action.newInventoryName,
        },
    },
})), on(INVENTORY_ACTIONS.setDateValidator, (state, action) => ({
    ...state,
    inventoryOptions: {
        ...state.inventoryOptions,
        validators: {
            ...state.inventoryOptions.validators,
            inventoryDate: action.newInventoryDate,
        },
    },
})), on(INVENTORY_ACTIONS.editName, (state, action) => {
    const inventory = state?.entities[action.inventoryId];
    if (!inventory) {
        return { ...state };
    }
    return inventoryAdapter.upsertOne({
        ...inventory,
        inventoryName: action.newInventoryName,
    }, state);
}), on(INVENTORY_ACTIONS.searchInInventoryWorksheet, (state, action) => ({
    ...state,
    inventoryOptions: {
        ...state.inventoryOptions,
        searchKey: action.searchKey,
    },
})), on(INVENTORY_ACTIONS.searchInAllInventories, (state, action) => ({
    ...state,
    allInventoriesSearchKey: action.searchKey,
})), on(INVENTORY_ACTIONS.complete, (state, action) => {
    const inventoryToComplete = state.entities[action.inventoryId];
    if (inventoryToComplete === undefined) {
        return { ...state };
    }
    const inventory = generateMutableInventory(inventoryToComplete);
    inventory.status = InventoryStatus.COMPLETED;
    return inventoryAdapter.upsertOne(inventory, state);
}), on(INVENTORY_ACTIONS.completeSuccess, (state, action) => {
    // Pulling latest state because we only want to update specific values from api.
    const inventory = state?.entities[action.inventory.inventoryId];
    if (!inventory) {
        return { ...state };
    }
    const completedInventory = generateMutableInventory(inventory);
    completedInventory.ecomUpdateDtm = action.inventory.ecomUpdateDtm;
    completedInventory.ecomUpdateUserId = action.inventory.ecomUpdateUserId;
    completedInventory.updatedByUser = action.inventory.updatedByUser;
    completedInventory.completedDtm = action.inventory.completedDtm;
    return inventoryAdapter.upsertOne(completedInventory, state);
}), on(INVENTORY_ACTIONS.toggleShowCompleted, (state, action) => ({
    ...state,
    inventoryOptions: {
        ...state.inventoryOptions,
        showCompleted: action.showCompleted,
    },
})), on(INVENTORY_ACTIONS.resetAllDuplicatesFlag, (state) => ({
    ...state,
    inventoryOptions: {
        ...state.inventoryOptions,
        allDuplicates: false,
    },
})), on(INVENTORY_ACTIONS.resetProductsAddedInventoryOption, (state, action) => ({
    ...state,
    inventoryOptions: {
        ...state.inventoryOptions,
        productsAdded: undefined,
    },
})), on(INVENTORY_ACTIONS.updateInventoryInformationModal, (state, action) => ({
    ...state,
    inventoryOptions: {
        ...state.inventoryOptions,
        isInfoModalOpen: action.toggle,
    },
})), on(INVENTORY_ACTIONS.updateInventorySortModal, (state, action) => ({
    ...state,
    inventoryOptions: {
        ...state.inventoryOptions,
        isSortModalOpen: action.toggle,
    },
})), 
/*  INVENTORY GROUP */
on(INVENTORY_GROUP_ACTIONS.create, (state, action) => {
    const inventory = state?.entities[action.inventoryId];
    if (!inventory) {
        return { ...state };
    }
    let updatedInventory = generateMutableInventory(inventory);
    updatedInventory = {
        ...updatedInventory,
        inventoryWorksheetCountSort: setInventoryWorksheetCountSort(updatedInventory.inventoryWorksheetCountSort, action.inventoryGroup.groupName),
        inventoryWorksheetEditSort: setInventoryWorksheetEditSort(updatedInventory.inventoryWorksheetEditSort, action.inventoryGroup.groupName),
    };
    if (!updatedInventory.groups) {
        updatedInventory.groups = [action.inventoryGroup];
    }
    else {
        updatedInventory.groups.unshift(action.inventoryGroup);
    }
    return inventoryAdapter.upsertOne(updatedInventory, state);
}), on(INVENTORY_GROUP_ACTIONS.update, (state, action) => {
    const inventory = state?.entities[action.inventoryId];
    if (!inventory) {
        return { ...state };
    }
    let updatedInventory = generateMutableInventory(inventory);
    if (updatedInventory.groups) {
        for (let i = 0; i < updatedInventory.groups?.length; i++) {
            if (updatedInventory.groups[i].groupName ===
                action.inventoryGroup.groupName) {
                updatedInventory.groups[i].groupName = action.newGroupName;
            }
        }
    }
    updatedInventory = {
        ...updatedInventory,
        inventoryWorksheetCountSort: setInventoryWorksheetCountSort(updatedInventory.inventoryWorksheetCountSort, action.newGroupName),
        inventoryWorksheetEditSort: setInventoryWorksheetEditSort(updatedInventory.inventoryWorksheetEditSort, action.newGroupName),
    };
    return inventoryAdapter.upsertOne(updatedInventory, state);
}), on(INVENTORY_GROUP_ACTIONS.delete, (state, action) => {
    const inventory = state.entities[action.inventoryId];
    if (!inventory) {
        return { ...state };
    }
    const updatedInventory = generateMutableInventory(inventory);
    if (action.inventoryGroup.groupAbsoluteIndex !== undefined) {
        updatedInventory.groups?.splice(action.inventoryGroup.groupAbsoluteIndex, 1);
    }
    return inventoryAdapter.upsertOne(updatedInventory, {
        ...state,
        inventoryOptions: {
            ...state.inventoryOptions,
            selectedGroup: action.groupToFocus,
        },
    });
}), on(INVENTORY_ACTIONS.deleteSuccess, (state, action) => inventoryAdapter.removeOne(action.inventoryId, state)), on(INVENTORY_ACTIONS.deleteFail, (state, action) => ({
    ...state,
    error: action.error,
})), on(INVENTORY_ACTIONS.updateInventoryCustomUOMModal, (state, action) => ({
    ...state,
    inventoryOptions: {
        ...state.inventoryOptions,
        isCustomUOMOpen: action.toggle,
    },
})), on(INVENTORY_ACTIONS.loadCustomerMigrationSuccess, (state, action) => ({
    ...state,
    customerMigrationStatus: action.customerMigration,
})), on(INVENTORY_ACTIONS.loadCustomerMigrationFail, (state, action) => ({
    ...state,
    error: action.error,
})), on(INVENTORY_ACTIONS.initiateMigration, (state, action) => ({
    ...state,
    customerMigrationStatus: {
        ...state.customerMigrationStatus,
        customerMigrated: MigrationStatus.IN_PROGRESS,
    },
})), 
/* INVENTORY ITEM */
on(INVENTORY_ITEM_ACTIONS.update, (state, action) => {
    const inventorySelected = state.entities[action.inventoryId];
    if (inventorySelected === undefined) {
        return { ...state };
    }
    const inventory = generateMutableInventory(inventorySelected);
    if (inventory.groups && inventory.groups.length > 0) {
        const groupIndex = inventory.groups.findIndex((value) => value.groupName === action.groupName);
        const group = inventory.groups[groupIndex];
        if (groupIndex !== undefined && group) {
            const itemIndex = group.groupItems?.findIndex((value) => value.itemNumber === action.inventoryItem.itemNumber);
            if (itemIndex !== undefined && group.groupItems) {
                group.groupItems[itemIndex] = action.inventoryItem;
                inventory.groups[groupIndex] = group;
            }
        }
    }
    return inventoryAdapter.upsertOne(inventory, state);
}), on(INVENTORY_ITEM_ACTIONS.selectInventoryItem, (state, action) => ({
    ...state,
    inventoryOptions: {
        ...state.inventoryOptions,
        selections: updateSelection({
            inventoryId: action.inventoryId,
            inventoryItemId: action.inventoryItemId,
            groupName: action.groupName,
            selection: action.selection,
            isCatchWeight: action.isCatchWeight,
        }, state.inventoryOptions.selections.slice()),
    },
})), on(INVENTORY_ITEM_ACTIONS.resetInventoryItemSelections, (state, action) => ({
    ...state,
    inventoryOptions: {
        ...state.inventoryOptions,
        selections: state.inventoryOptions.selections
            .slice()
            .filter((selection) => selection.inventoryId !== action.inventoryId),
    },
})), on(INVENTORY_ITEM_ACTIONS.selectAllInventoryItems, (state, action) => {
    const inventorySelected = state.entities[action.inventoryId];
    if (inventorySelected === undefined) {
        return { ...state };
    }
    const inventory = generateMutableInventory(inventorySelected);
    return {
        ...state,
        inventoryOptions: {
            ...state.inventoryOptions,
            selections: selectAllInventoryItemsByGroup(inventory, action.selectedGroup),
        },
    };
}), on(INVENTORY_ITEM_ACTIONS.updateUOM, (state, action) => {
    const inventoryToCopy = state.entities[action.inventoryId];
    if (inventoryToCopy === undefined) {
        return { ...state };
    }
    let inventory = generateMutableInventory(inventoryToCopy);
    inventory = operateClosureOnInventory(inventory, action.groupName, action.inventoryItemId, uomUpdateFunction, action.uom);
    return inventoryAdapter.upsertOne(inventory, state);
}), on(INVENTORY_ITEM_ACTIONS.resetUOM, (state, action) => {
    const inventoryToCopy = state.entities[action.inventoryId];
    if (inventoryToCopy === undefined) {
        return { ...state };
    }
    let inventory = generateMutableInventory(inventoryToCopy);
    inventory = operateClosureOnInventory(inventory, action.groupName, action.inventoryItemId, uomResetFunction, '');
    return inventoryAdapter.upsertOne(inventory, state);
}), on(INVENTORY_ITEM_ACTIONS.updateCustomConversionFactorUnit, (state, action) => {
    const inventoryToCopy = state.entities[action.inventoryId];
    if (inventoryToCopy === undefined) {
        return { ...state };
    }
    let inventory = generateMutableInventory(inventoryToCopy);
    inventory = operateClosureOnInventory(inventory, action.groupName, action.inventoryItemId, customUOMUpdateFunction, action.customUnit);
    return inventoryAdapter.upsertOne(inventory, state);
}), on(INVENTORY_ITEM_ACTIONS.updateQuantity, (state, action) => {
    const inventoryToCopy = state.entities[action.inventoryId];
    if (inventoryToCopy === undefined) {
        return { ...state };
    }
    let inventory = generateMutableInventory(inventoryToCopy);
    inventory = operateClosureOnInventory(inventory, action.groupName, action.inventoryItemId, quantityUpdateFunction, action.value, action.isCase);
    return inventoryAdapter.upsertOne(inventory, state);
}), on(INVENTORY_ITEM_ACTIONS.updatePriceOverride, (state, action) => {
    const inventoryToCopy = state.entities[action.inventoryId];
    if (inventoryToCopy === undefined) {
        return { ...state };
    }
    let inventory = generateMutableInventory(inventoryToCopy);
    inventory = operateClosureOnInventory(inventory, action.groupName, action.inventoryItemId, priceOverrideFunction, action.overrideValue);
    return inventoryAdapter.upsertOne(inventory, state);
}), on(INVENTORY_ITEM_ACTIONS.updateInventoryItemModalOptions, (state, action) => {
    const modalOptions = {
        ...state.inventoryItemModalOptions,
        ...action.inventoryItemModalOptions,
    };
    const checkPosition = modalOptions.selectedPosition !== '';
    const newGroupSelectedWithPosition = modalOptions.initialGroup.groupName !==
        modalOptions.selectedGroup.groupName && checkPosition;
    const oldGroupWithPosition = modalOptions.initialGroup.groupName ===
        modalOptions.selectedGroup.groupName && checkPosition;
    let checkUnit = false;
    const checkConversionChange = !modalOptions.catchWeightFlag &&
        modalOptions.initialInvConvFactor !== modalOptions.invConvFactor &&
        !(modalOptions.initialInvConvFactor === undefined &&
            (modalOptions.invConvFactor === undefined ||
                modalOptions.invConvFactor === 0));
    if (!!modalOptions.unit && modalOptions.unit !== 'Custom') {
        checkUnit = modalOptions.initialUnit !== modalOptions.unit;
    }
    let checkOverrideChange = false;
    if (modalOptions.initialPriceOverrideInd === 'Y') {
        checkOverrideChange =
            modalOptions.priceOverrideInd === 'N' ||
                (modalOptions.initialOverrideCasePrice !==
                    modalOptions.overrideCasePrice &&
                    !(modalOptions.overrideCasePrice === undefined ||
                        modalOptions.overrideCasePrice === 0));
    }
    else {
        checkOverrideChange =
            modalOptions.initialOverrideCasePrice !==
                modalOptions.overrideCasePrice &&
                modalOptions.overrideCasePrice !== undefined &&
                modalOptions.overrideCasePrice > 0;
    }
    let customUnitError = false;
    let customUnitSaveCheck = false;
    if (!!modalOptions.customUnit &&
        (modalOptions.unit === 'Custom' ||
            modalOptions.unit === modalOptions.initialCustomUnit)) {
        customUnitError = BASE_INVENTORY_UNITS.includes(modalOptions.customUnit.toUpperCase());
        customUnitSaveCheck =
            modalOptions.customUnit !== '' &&
                (modalOptions.customUnit.toUpperCase() !==
                    modalOptions.initialCustomUnit.toUpperCase() ||
                    (modalOptions.initialUnit.toUpperCase() !==
                        modalOptions.customUnit.toUpperCase() &&
                        modalOptions.customUnit.toUpperCase() ===
                            modalOptions.initialCustomUnit.toUpperCase())) &&
                !BASE_INVENTORY_UNITS.includes(modalOptions.customUnit.toUpperCase());
    }
    return {
        ...state,
        inventoryItemModalOptions: {
            ...modalOptions,
            saveButtonIsDisabled: !(newGroupSelectedWithPosition ||
                oldGroupWithPosition ||
                checkUnit ||
                checkOverrideChange ||
                checkConversionChange ||
                customUnitSaveCheck),
            customErrorFlag: customUnitError,
        },
    };
}), on(INVENTORY_ITEM_ACTIONS.saveInventoryItemModalOptions, (state, action) => {
    const inventoryToCopy = state.entities[state.inventoryItemModalOptions.inventoryId];
    if (inventoryToCopy === undefined) {
        return { ...state };
    }
    let inventory = generateMutableInventory(inventoryToCopy);
    inventory = operateClosureOnInventory(inventory, state.inventoryItemModalOptions.initialGroup.groupName, state.inventoryItemModalOptions.inventoryItemId, saveInventoryItemModal, state.inventoryItemModalOptions);
    return inventoryAdapter.upsertOne(inventory, state);
}), on(INVENTORY_ITEM_ACTIONS.updateConversionFactor, (state, action) => {
    const inventoryToCopy = state.entities[action.inventoryId];
    if (inventoryToCopy === undefined) {
        return { ...state };
    }
    let inventory = generateMutableInventory(inventoryToCopy);
    inventory = operateClosureOnInventory(inventory, action.groupName, action.inventoryItemId, conversionFactorFunction, action.conversionValue);
    return inventoryAdapter.upsertOne(inventory, state);
}), on(INVENTORY_ITEM_ACTIONS.updateUOMAndConversionFactor, (state, action) => {
    const inventoryToCopy = state.entities[action.inventoryId];
    if (inventoryToCopy === undefined) {
        return { ...state };
    }
    let inventory = generateMutableInventory(inventoryToCopy);
    const itemMap = new Map();
    action.itemsToUpdate.forEach((item) => {
        if (!itemMap.get(item.groupName)) {
            itemMap.set(item.groupName, []);
        }
        itemMap.get(item.groupName)?.push(item.inventoryItemId);
    });
    const newUOMandConversion = {
        uom: action.uom,
        conversionValue: action.conversionValue,
        customUOM: action.customUOM,
    };
    inventory = operateClosureOnInventoryToMultipleItems(inventory, itemMap, uomAndConversionUpdateFunction, newUOMandConversion);
    return inventoryAdapter.upsertOne(inventory, state);
}), on(INVENTORY_ITEM_ACTIONS.moveInventoryItemsIntoGroup, INVENTORY_GROUP_ACTIONS.deleteAndMoveItems, (state, action) => {
    const inventoryToCopy = state.entities[action.inventoryId];
    if (inventoryToCopy === undefined) {
        return { ...state };
    }
    const inventory = generateMutableInventory(inventoryToCopy);
    const inventoryItemsToMove = [];
    let indexToMutate = action.targetGroupAndIndex.inventoryItemIndex;
    let mutablePairs = [];
    action.inventoryItemIdAndGroupPairs.forEach((itemPair) => {
        mutablePairs.push({ ...itemPair });
    });
    const updateAfterIndex = (indexFrom, itemIndex, groupIndex, pairs) => {
        for (let i = indexFrom; i < pairs.length; i++) {
            if (pairs[i].groupIndex === groupIndex) {
                if (pairs[i].inventoryItemIndex > itemIndex) {
                    pairs[i].inventoryItemIndex--;
                }
            }
        }
        return pairs;
    };
    // Go through all the item pairs and pull from groups the items.
    // Update the index of all pairs inside of a group.
    // Add the pulled items to a list preparing to check for duplicate checks.
    if (inventory.groups && inventory.groups.length > 0) {
        for (let index = 0; index < action.inventoryItemIdAndGroupPairs.length; index++) {
            const pair = mutablePairs[index];
            const groupMatchedToPair = inventory.groups[pair.groupIndex];
            if (groupMatchedToPair?.groupItems &&
                groupMatchedToPair.groupItems?.length > 0) {
                const itemMatchedToPair = groupMatchedToPair.groupItems[pair.inventoryItemIndex];
                if (itemMatchedToPair) {
                    inventoryItemsToMove.push(itemMatchedToPair);
                    if (pair.groupIndex === action.targetGroupAndIndex.groupIndex &&
                        pair.inventoryItemIndex < indexToMutate) {
                        indexToMutate--;
                    }
                    mutablePairs = updateAfterIndex(index + 1, pair.inventoryItemIndex, pair.groupIndex, mutablePairs);
                    groupMatchedToPair.groupItems.splice(pair.inventoryItemIndex, 1);
                }
                inventory.groups[pair.groupIndex] = groupMatchedToPair;
            }
        }
        // Check for duplicates if a duplicate is found.
        // We add the counts to the existing item.
        // Replace the existing items to overrides with the selected item.
        // Queue up selected item to be dropped from the items to move list.
        const itemsToMerge = new Map();
        inventoryItemsToMove.forEach((itemToCompare) => {
            const items = inventory.groups[action.targetGroupAndIndex.groupIndex].groupItems;
            if (items) {
                let itemToKeep = items.find((itemToCheck) => itemToCheck.itemNumber === itemToCompare.itemNumber);
                // Either we merge values in place,
                // Add the item to our potential merge map,
                // Or merge the item into our map of items for final item injection.
                if (itemToKeep) {
                    const itemIndex = items.indexOf(itemToKeep);
                    itemToKeep = mergeInventoryItems(itemToCompare, itemToKeep);
                    items[itemIndex] = itemToKeep;
                    inventory.groups[action.targetGroupAndIndex.groupIndex].groupItems = items;
                }
                else {
                    itemToKeep = itemsToMerge.get(itemToCompare.itemNumber);
                    if (itemToKeep) {
                        itemToKeep = mergeInventoryItems(itemToCompare, itemToKeep);
                        itemsToMerge.set(itemToCompare.itemNumber, itemToKeep);
                    }
                    else {
                        itemsToMerge.set(itemToCompare.itemNumber, itemToCompare);
                    }
                }
            }
        });
        const finalList = [];
        for (const item of itemsToMerge.values()) {
            finalList.push(item);
        }
        // Adding items from other groups that are selected into the targeted group at targeted position.
        // Comparing group to desired index to know whether to push/unshift/splice
        if (indexToMutate === 0) {
            inventory.groups[action.targetGroupAndIndex.groupIndex].groupItems?.unshift(...finalList);
        }
        else if (indexToMutate >=
            (inventory.groups[action.targetGroupAndIndex.groupIndex].groupItems?.length ?? 0)) {
            inventory.groups[action.targetGroupAndIndex.groupIndex].groupItems?.push(...finalList);
        }
        else {
            inventory.groups[action.targetGroupAndIndex.groupIndex].groupItems?.splice(indexToMutate, 0, ...finalList);
        }
    }
    return inventoryAdapter.upsertOne(inventory, state);
}), on(INVENTORY_GROUP_ACTIONS.deleteAndMoveItems, (state, action) => {
    const inventory = state.entities[action.inventoryId];
    if (!inventory) {
        return { ...state };
    }
    const updatedInventory = generateMutableInventory(inventory);
    if (action.inventoryItemIdAndGroupPairs.length > 0) {
        updatedInventory.groups?.splice(action.inventoryItemIdAndGroupPairs[0].groupIndex, 1);
    }
    return inventoryAdapter.upsertOne(updatedInventory, {
        ...state,
        inventoryOptions: {
            ...state.inventoryOptions,
            selectedGroup: action.groupToFocus,
        },
    });
}), on(INVENTORY_GROUP_ACTIONS.move, (state, action) => {
    const inventory = state.entities[action.inventoryId];
    if (!inventory) {
        return { ...state };
    }
    const updatedInventory = generateMutableInventory(inventory);
    const groupRemovedArray = updatedInventory.groups?.splice(action.previousIndex, 1);
    if (groupRemovedArray) {
        updatedInventory.groups?.splice(action.newIndex, 0, groupRemovedArray[0]);
    }
    return inventoryAdapter.upsertOne(updatedInventory, state);
}), on(INVENTORY_ITEM_ACTIONS.deleteInventoryItemsInGroup, (state, action) => {
    const inventoryFromState = state.entities[action.inventoryId];
    if (inventoryFromState === undefined) {
        return { ...state };
    }
    const inventory = generateMutableInventory(inventoryFromState);
    const inventoryItemsToDelete = [];
    let tobeDeletedIndexes = [];
    action.inventoryItemIdAndGroupPairs.forEach((itemPair) => {
        tobeDeletedIndexes.push({ ...itemPair });
    });
    const updateIndexesAfterEachDeletion = (indexFrom, itemIndex, groupIndex, pairs) => {
        for (let i = indexFrom; i < pairs.length; i++) {
            if (pairs[i].groupIndex === groupIndex) {
                if (pairs[i].inventoryItemIndex > itemIndex) {
                    pairs[i].inventoryItemIndex--;
                }
            }
        }
        return pairs;
    };
    if (inventory.groups && inventory.groups.length > 0) {
        for (let index = 0; index < action.inventoryItemIdAndGroupPairs.length; index++) {
            const pair = tobeDeletedIndexes[index];
            const groupMatchedToPair = inventory.groups[pair.groupIndex];
            if (groupMatchedToPair?.groupItems &&
                groupMatchedToPair.groupItems?.length > 0) {
                const itemMatchedToPair = groupMatchedToPair.groupItems[pair.inventoryItemIndex];
                if (itemMatchedToPair) {
                    /** Updates the indexes of requested pairs after each deletion,
                      eg: Requested group1 and item indexes 2,5,6 . Then after first removal of index 2,
                      we are adjusting the item indexes for rest of the requested  5,6 ==> 4,5
                     **/
                    tobeDeletedIndexes = updateIndexesAfterEachDeletion(index + 1, pair.inventoryItemIndex, pair.groupIndex, tobeDeletedIndexes);
                    groupMatchedToPair.groupItems.splice(pair.inventoryItemIndex, 1);
                }
                inventory.groups[pair.groupIndex] = groupMatchedToPair;
            }
        }
    }
    return inventoryAdapter.upsertOne(inventory, state);
}), on(INVENTORY_ITEM_ACTIONS.moveInventoryItemByLineNumber, (state, action) => {
    const inventoryToCopy = state.entities[action.inventoryId];
    if (inventoryToCopy === undefined) {
        return { ...state };
    }
    const inventory = generateMutableInventory(inventoryToCopy);
    let lineNumber = 1;
    let itemToMove;
    let targetGroup;
    let originalGroup;
    let originalItemIndex = 0;
    let targetItemIndex = 0;
    let originalGroupIndex = 0;
    let targetGroupIndex = 0;
    if (inventory.groups) {
        // If user enters number greater than inventory length, move item to last group at the last index
        if (action.newLineNumber > action.numberOfProducts) {
            targetGroupIndex = inventory.groups.length - 1;
            targetGroup = inventory.groups[targetGroupIndex];
            if (targetGroup.groupItems) {
                targetItemIndex = targetGroup.groupItems.length;
            }
        }
        let groupIndex = 0;
        for (const group of inventory.groups) {
            if (group.groupItems) {
                let itemIndex = 0;
                for (const item of group.groupItems) {
                    if (lineNumber === action.previousLineNumber) {
                        itemToMove = item;
                        originalGroup = group;
                        originalItemIndex = itemIndex;
                        originalGroupIndex = groupIndex;
                    }
                    if (lineNumber === action.newLineNumber && !targetGroup) {
                        targetGroup = group;
                        targetGroupIndex = groupIndex;
                        targetItemIndex = itemIndex;
                    }
                    itemIndex++;
                    lineNumber++;
                }
            }
            groupIndex++;
        }
    }
    let isDuplicateProduct = false;
    if (originalGroup && targetGroup && itemToMove) {
        if (originalGroup?.groupName === targetGroup?.groupName) {
            if (targetGroup.groupItems) {
                const newItemsArray = targetGroup.groupItems;
                newItemsArray.splice(originalItemIndex, 1);
                newItemsArray.splice(targetItemIndex, 0, itemToMove);
                targetGroup.groupItems = newItemsArray;
                if (inventory.groups) {
                    inventory.groups[targetGroupIndex] = targetGroup;
                }
            }
        }
        else if (originalGroup?.groupName !== targetGroup?.groupName) {
            // if target group is below group we move from, have to add one to originalItemIndex
            if (action.newLineNumber > action.previousLineNumber &&
                action.newLineNumber <= action.numberOfProducts) {
                targetItemIndex++;
            }
            if (originalGroup.groupItems && targetGroup.groupItems) {
                const newItemsForOriginalGroup = originalGroup.groupItems;
                const newItemsForTargetGroup = targetGroup.groupItems;
                newItemsForOriginalGroup.splice(originalItemIndex, 1);
                newItemsForTargetGroup.splice(targetItemIndex, 0, itemToMove);
                originalGroup.groupItems = newItemsForOriginalGroup;
                targetGroup.groupItems = newItemsForTargetGroup;
                if (inventory.groups) {
                    inventory.groups[targetGroupIndex] = targetGroup;
                    inventory.groups[originalGroupIndex] = originalGroup;
                }
                let duplicateItemCount = 0;
                for (const item of targetGroup.groupItems) {
                    if (item.itemNumber === itemToMove.itemNumber) {
                        duplicateItemCount++;
                    }
                }
                if (duplicateItemCount > 1) {
                    isDuplicateProduct = true;
                }
            }
        }
    }
    if (isDuplicateProduct) {
        return {
            ...state,
            inventoryOptions: {
                ...state.inventoryOptions,
                allDuplicates: true,
            },
        };
    }
    else {
        return inventoryAdapter.upsertOne(inventory, state);
    }
}), on(INVENTORY_ITEM_ACTIONS.copyInventoryItemsIntoGroup, (state, action) => {
    const inventoryToCopy = state.entities[action.inventoryId];
    if (inventoryToCopy === undefined) {
        return { ...state };
    }
    const inventory = generateMutableInventory(inventoryToCopy);
    const inventoryItemsToCopy = [];
    const mutablePairs = action.inventoryItemIdAndGroupPairs;
    // Go through all the item pairs and pull from selected group the items.
    if (inventory.groups && inventory.groups.length > 0) {
        for (let index = 0; index < action.inventoryItemIdAndGroupPairs.length; index++) {
            const pair = mutablePairs[index];
            const groupMatchedToPair = inventory.groups[pair.groupIndex];
            if (groupMatchedToPair?.groupItems &&
                groupMatchedToPair.groupItems?.length > 0) {
                const itemMatchedToPair = groupMatchedToPair.groupItems[pair.inventoryItemIndex];
                if (itemMatchedToPair) {
                    inventoryItemsToCopy.push(itemMatchedToPair);
                }
            }
        }
        // Use the invalid keys to drop duplicate items
        // Reset the counts for items to copy
        // To avoid copying duplicate product numbers, as we add products their number is added to invalid keys list
        let invalidKeys = inventory.groups[action.targetGroupAndIndex.groupIndex].groupItems?.map((item) => item.itemNumber);
        let copyNonDuplicateItems = inventoryItemsToCopy.filter((itemToCopy) => {
            if (invalidKeys !== undefined) {
                if (invalidKeys.includes(itemToCopy.itemNumber)) {
                    return false;
                }
                else {
                    invalidKeys.push(itemToCopy.itemNumber);
                    return true;
                }
            }
            else {
                invalidKeys = [];
                invalidKeys.push(itemToCopy.itemNumber);
                return true;
            }
        });
        // if all items to copy are duplicates we send allDuplicates in true to show the red toast
        if (copyNonDuplicateItems.length === 0) {
            return {
                ...state,
                inventoryOptions: {
                    ...state.inventoryOptions,
                    allDuplicates: true,
                },
            };
        }
        copyNonDuplicateItems = copyNonDuplicateItems.map((itemToCopy) => ({
            ...itemToCopy,
            caseOnHand: 0,
            eachOnHand: 0,
            caseOrd: 0,
            eachOrd: 0,
        }));
        // Adding items from other groups that are selected into the targeted gorup at targeted position.
        // Add the copied items to the selected group
        inventory.groups[action.targetGroupAndIndex.groupIndex].groupItems?.unshift(...copyNonDuplicateItems);
    }
    return inventoryAdapter.upsertOne(inventory, state);
}), on(INVENTORY_ITEM_ACTIONS.addInventoryItemsIntoGroup, (state, action) => {
    const inventory = state.entities[action.inventoryId];
    if (inventory === undefined) {
        return { ...state };
    }
    const inventoryToUpdate = generateMutableInventory(inventory);
    const group = inventoryToUpdate.groups?.find((check) => check.groupName === action.groupName);
    let productAddCount = 0;
    if (group) {
        if (group.groupItems) {
            const itemsToAdd = action.inventoryItems.filter((item) => {
                const itemFind = group.groupItems?.find((groupItem) => groupItem.itemNumber === item.itemNumber);
                return itemFind === undefined;
            });
            if (action.placeTop) {
                group.groupItems?.unshift(...itemsToAdd);
            }
            else {
                group.groupItems?.push(...itemsToAdd);
            }
            productAddCount = itemsToAdd.length;
        }
        else {
            group.groupItems = action.inventoryItems;
            productAddCount = action.inventoryItems.length;
        }
        inventoryToUpdate.groups[inventoryToUpdate.groups.findIndex((check) => check.groupName === action.groupName)] = group;
    }
    return inventoryAdapter.upsertOne(inventoryToUpdate, {
        ...state,
        inventoryOptions: {
            ...state.inventoryOptions,
            productsAdded: productAddCount,
        },
    });
}), on(INVENTORY_ITEM_ACTIONS.nonUSFValueUpdateSuccess, (state, action) => {
    const inventoryList = [];
    action.inventoryIds.forEach((id) => {
        if (state.entities[id] !== undefined) {
            inventoryList.push(state.entities[id]);
        }
    });
    const inventoriesToReturn = [];
    inventoryList.forEach((inventoryFromState) => {
        const inventory = generateMutableInventory(inventoryFromState);
        inventory.groups?.forEach((group) => {
            let groupNeedsUpdate = false;
            group.groupItems?.forEach((inventoryItem) => {
                // Check each item in action.items array for a match
                const itemMap = new Map(action.items.map((item) => [item.nonUsfProductNumber, item]));
                const matchingItem = itemMap.get(inventoryItem.itemNumber);
                if (matchingItem) {
                    inventoryItem.productCasePrice =
                        matchingItem.productCasePrice ?? -1;
                    inventoryItem.productConvFactor =
                        matchingItem.productConvFactor ?? 1;
                    inventoryItem.productPackSizeLabel =
                        matchingItem.productPackSizeLabel ?? '';
                    inventoryItem.productUnitOfMeasure =
                        matchingItem.productUnitOfMeasure ?? 'EA';
                    inventoryItem.itemUpdateDtm = action.timeStamp;
                    groupNeedsUpdate = true;
                }
            });
            if (groupNeedsUpdate) {
                group.groupUpdateDtm = action.timeStamp;
            }
        });
        inventory.ecomUpdateDtm = action.timeStamp;
        inventoriesToReturn.push({
            ...inventory,
        });
    });
    return inventoryAdapter.upsertMany(inventoriesToReturn, state);
}), 
//This action should be dispatched before updating the inventory
on(INVENTORY_ACTIONS.updateInventorySnapshot, (state, action) => {
    const inventory = state.entities[action.inventoryId];
    return {
        ...state,
        inventoryOptions: {
            ...state.inventoryOptions,
            inventorySnapshot: inventory,
        },
    };
}), on(INVENTORY_ACTIONS.completeFail, INVENTORY_ACTIONS.updateFail, INVENTORY_ACTIONS.updateWithToastMessageFail, INVENTORY_ACTIONS.updateWithTracingFail, INVENTORY_GROUP_ACTIONS.createFail, (state, action) => {
    if (state?.inventoryOptions?.inventorySnapshot !== undefined) {
        const inventoryToRollBack = state.inventoryOptions
            .inventorySnapshot;
        return inventoryAdapter.upsertOne(inventoryToRollBack, state);
    }
    else {
        return {
            ...state,
        };
    }
}), on(INVENTORY_ACTIONS.completeSuccess, INVENTORY_ACTIONS.updateSuccess, INVENTORY_ACTIONS.updateWithToastMessageSuccess, INVENTORY_ACTIONS.updateWithTracingSuccess, INVENTORY_GROUP_ACTIONS.createSuccess, (state, action) => ({
    ...state,
    inventoryOptions: {
        ...state.inventoryOptions,
        inventorySnapshot: undefined,
    },
})), on(INVENTORY_ITEM_ACTIONS.bulkItemUpdate, (state, action) => {
    const inventory = state.entities[action.bulkUpdatePayload.inventoryId];
    if (!inventory) {
        return state;
    }
    const itemLookup = createInventoryItemLookup(inventory);
    const updatedPendingUpdates = updatePendingItemState(state, action.bulkUpdatePayload.inventoryId, action.bulkUpdatePayload.updates, itemLookup);
    const updatedGroups = updateInventoryGroups(inventory, action.bulkUpdatePayload.inventoryId, action.bulkUpdatePayload.updates);
    const updatedMissedItems = clearMissedItemUpdates(state.missedItemUpdates, action.bulkUpdatePayload.updates, action.bulkUpdatePayload.inventoryId);
    return inventoryAdapter.upsertOne({ ...inventory, groups: updatedGroups }, {
        ...state,
        pendingItemUpdates: updatedPendingUpdates,
        missedItemUpdates: updatedMissedItems,
    });
}), on(INVENTORY_ITEM_ACTIONS.setBulkSyncing, (state, action) => {
    const updatedPendingUpdates = { ...state.pendingItemUpdates };
    action.updates.forEach((update) => {
        const key = generatePendingItemUpdateKey(update);
        const currentPendingUpdateState = updatedPendingUpdates[key];
        if (!!currentPendingUpdateState) {
            updatedPendingUpdates[key] = {
                pendingItemUpdates: currentPendingUpdateState.pendingItemUpdates,
                isSyncing: action.isSyncing,
            };
        }
    });
    return {
        ...state,
        pendingItemUpdates: updatedPendingUpdates,
    };
}), on(INVENTORY_ITEM_ACTIONS.incrementRetryCount, (state, action) => {
    const updatedPendingUpdates = { ...state.pendingItemUpdates };
    action.updates.forEach((update) => {
        const key = generatePendingItemUpdateKey(update);
        const currentPendingUpdateState = updatedPendingUpdates[key];
        if (!!currentPendingUpdateState) {
            updatedPendingUpdates[key] = {
                isSyncing: currentPendingUpdateState.isSyncing,
                pendingItemUpdates: currentPendingUpdateState.pendingItemUpdates.map((pendingUpdate) => ({
                    ...pendingUpdate,
                    retryCount: pendingUpdate.retryCount + 1,
                })),
            };
        }
    });
    return {
        ...state,
        pendingItemUpdates: updatedPendingUpdates,
    };
}), on(INVENTORY_ITEM_ACTIONS.bulkItemUpdateSuccess, (state, action) => {
    const inventoryFromState = state.entities[action.inventory.inventoryId];
    if (!inventoryFromState) {
        return inventoryAdapter.upsertOne(action.inventory, {
            ...state,
            pendingItemUpdates: { ...state.pendingItemUpdates },
        });
    }
    const apiItemLookup = createInventoryItemLookup(action.inventory);
    const stateItemLookup = createInventoryItemLookup(inventoryFromState);
    const updatedPendingUpdates = { ...state.pendingItemUpdates };
    // update missed item updates in state
    const missedItemUpdates = updateMissedItemUpdates(updatedPendingUpdates, apiItemLookup, stateItemLookup, action.missedItems, state.missedItemUpdates);
    // remove pending updates for missed items
    const processedItems = processMissedItems(updatedPendingUpdates, action.missedItems, action.inventory.inventoryId);
    // remove pending updates for items if all pending updates are processed
    processRemainingUpdates(updatedPendingUpdates, apiItemLookup, action.inventory.inventoryId, processedItems);
    // merge inventory state with API information
    const mergedInventory = mergeInventoryStateWithAPIInformation(inventoryFromState, action.inventory, updatedPendingUpdates);
    return inventoryAdapter.upsertOne(mergedInventory, {
        ...state,
        pendingItemUpdates: updatedPendingUpdates,
        missedItemUpdates,
    });
}), on(INVENTORY_ITEM_ACTIONS.revertFailedUpdates, (state, action) => {
    const inventory = state.entities[action.updates[0].inventoryId];
    if (!inventory) {
        return state;
    }
    const originalValues = getOriginalValuesForFailedUpdates(state.pendingItemUpdates, action.updates);
    const updatedGroups = revertInventoryGroups(inventory, action.updates[0].inventoryId, originalValues);
    return inventoryAdapter.upsertOne({ ...inventory, groups: updatedGroups }, state);
}), on(INVENTORY_ITEM_ACTIONS.clearFailedPendingUpdates, (state, action) => {
    const updatedPendingUpdates = { ...state.pendingItemUpdates };
    action.updates.forEach((update) => {
        const key = generatePendingItemUpdateKey(update);
        delete updatedPendingUpdates[key];
    });
    return {
        ...state,
        pendingItemUpdates: updatedPendingUpdates,
    };
}), on(INVENTORY_ITEM_ACTIONS.clearMissedItemUpdatesAfterTimeout, (state, action) => {
    const updatedMissedItemUpdates = { ...state.missedItemUpdates };
    action.missedItems.forEach((missedItem) => {
        const key = generatePendingItemUpdateKey(missedItem);
        delete updatedMissedItemUpdates[key];
    });
    return {
        ...state,
        missedItemUpdates: updatedMissedItemUpdates,
    };
}));
/**
 * setInventoryWorksheetSorts: generates and adds the inventoryWorksheetCountSort and inventoryWorksheetEditSort for each group in the given inventory.
 *
 * @param inventory
 * @returns the inventory with the inventoryWorksheetCountSort and inventoryWorksheetEditSort initialized
 */
const setInventoryWorksheetSorts = (inventory) => {
    let initialInventoryWorksheetCountSort = {};
    let initialInventoryWorksheetEditSort = {};
    inventory?.groups?.forEach((group) => {
        initialInventoryWorksheetCountSort = setInventoryWorksheetCountSort(initialInventoryWorksheetCountSort, group?.groupName);
        initialInventoryWorksheetEditSort = setInventoryWorksheetEditSort(initialInventoryWorksheetEditSort, group?.groupName);
    });
    return {
        ...inventory,
        inventoryWorksheetCountSort: initialInventoryWorksheetCountSort,
        inventoryWorksheetEditSort: initialInventoryWorksheetEditSort,
    };
};
/**
 * setInventoryWorksheetCountSort: generates inventoryWorksheetCountSort for each group in the given inventory.
 *
 * @param initialInventoryWorksheetCountSort
 * @param groupName
 *
 * @returns the initialized inventoryWorksheetCountSort
 */
const setInventoryWorksheetCountSort = (initialInventoryWorksheetCountSort, groupName) => ({
    ...initialInventoryWorksheetCountSort,
    [groupName]: {
        selectedHeader: InventoryWorksheetCountSortOptions.lineNum,
        headers: inventoryWorksheetCountHeaders,
    },
});
/**
 * setInventoryWorksheetEditSort: generates inventoryWorksheetEditSort for each group in the given inventory.
 *
 * @param initialInventoryWorksheetEditSort
 * @param groupName
 *
 * @returns the initialized inventoryWorksheetEditSort
 */
const setInventoryWorksheetEditSort = (initialInventoryWorksheetEditSort, groupName) => ({
    ...initialInventoryWorksheetEditSort,
    [groupName]: {
        selectedHeader: InventoryWorksheetEditSortOptions.lineNum,
        headers: inventoryWorksheetEditHeaders,
    },
});
/**
 * updateInventorySortHeaders: updates the selectedHeader's sortDirection value
 * and resets and other headers' sortDirection to their default value
 * EXCEPT for default header, b/c client logic sets the sort direction to the opposite value on first click.
 *
 * @param inventorySort
 * @param inventorySection
 * @param selectedHeader
 * @param defaultHeader
 * @param initialHeaders
 * @param sortDirection
 * @returns the given inventory sort with its sort headers' sort direction values updated
 */
const updateInventorySortHeaders = (inventorySort, inventorySection, selectedHeader, defaultHeader, initialHeaders, sortDirection) => selectedHeader === defaultHeader
    ? {
        ...initialHeaders,
        [selectedHeader]: {
            ...inventorySort[inventorySection].headers[selectedHeader],
            sortDirection,
        },
    }
    : {
        ...initialHeaders,
        [defaultHeader]: {
            ...inventorySort[inventorySection].headers[defaultHeader],
            sortDirection: resetDefaultHeaderSortDirection(defaultHeader, sortDirection),
        },
        [selectedHeader]: {
            ...inventorySort[inventorySection].headers[selectedHeader],
            sortDirection,
        },
    };
const updateAllWorksheetSortHeaders = (inventorySort, selectedHeader, sortDirection) => {
    let updatedSort = {};
    Object.entries(inventorySort).forEach(([key, value]) => {
        updatedSort = {
            ...updatedSort,
            [key]: {
                headers: {
                    ...value.headers,
                    [selectedHeader]: {
                        ...value.headers[selectedHeader],
                        sortDirection,
                    },
                },
                selectedHeader,
            },
        };
    });
    return updatedSort;
};
/**
 * resetDefaultHeaderSortDirection: resets the default sort header's sort direction to the opposite of its initial value
 * b/c client logic reverses sort direction on first click.
 *
 * @param defaultHeader
 * @param sortDirection
 * @returns the updated sort direction for the default header
 */
const resetDefaultHeaderSortDirection = (defaultHeader, sortDirection) => {
    switch (defaultHeader) {
        case InventoriesSortOptions.lastUpdated:
        case InventoriesSortOptions.created:
            sortDirection = SortDirections.asc;
            break;
        case InventoryWorksheetCountSortOptions.lineNum:
        case InventoryWorksheetEditSortOptions.lineNum:
            sortDirection = SortDirections.desc;
            break;
    }
    return sortDirection;
};
/**
 * saveInventoryItemModalFunction: updates the given inventory item's priceOverrideInd,
 * overrideCasePrice, invConvFactor, and convFactorUnit value.
 *
 * @param inventoryItem
 * @param actionValue
 */
const saveInventoryItemModal = (inventoryItem, actionValue) => {
    priceOverrideFunction(inventoryItem, actionValue.overrideCasePrice);
    if (inventoryItem.productCatchWeightFlag
        ? inventoryItem.overrideCasePrice !== inventoryItem.productUnitPrice &&
            inventoryItem.overrideCasePrice !== 0
        : inventoryItem.overrideCasePrice !== inventoryItem.productCasePrice &&
            inventoryItem.overrideCasePrice !== 0) {
        inventoryItem.priceOverrideInd = actionValue.priceOverrideInd;
    }
    conversionFactorFunction(inventoryItem, actionValue.invConvFactor);
    if ((actionValue.customUnit && actionValue.unit === 'Custom') ||
        actionValue.unit === inventoryItem.customConvFactorUnit) {
        customUOMUpdateFunction(inventoryItem, actionValue.customUnit);
    }
    else if (actionValue.unit !== 'Custom') {
        uomUpdateFunction(inventoryItem, actionValue.unit);
    }
};
/**
 * priceOverrideFunction: updates the given inventory item's priceOverrideInd value.
 *
 * @param inventoryItem
 * @param actionValue
 */
const priceOverrideFunction = (inventoryItem, actionValue) => {
    inventoryItem.overrideCasePrice = actionValue;
    if (actionValue === 0 ||
        (inventoryItem.productCatchWeightFlag
            ? inventoryItem.overrideCasePrice === inventoryItem.productUnitPrice
            : inventoryItem.overrideCasePrice === inventoryItem.productCasePrice)) {
        inventoryItem.priceOverrideInd = 'N';
    }
    else {
        inventoryItem.priceOverrideInd = 'Y';
    }
};
/**
 * conversionFactorFunction: sets the conversion factor value for the given inventory item.
 *
 * @param inventoryItem
 * @param actionValue
 */
const conversionFactorFunction = (inventoryItem, actionValue) => {
    if (inventoryItem.productBreakable &&
        inventoryItem.productUnitPrice !== -1 &&
        (inventoryItem.productUnitPrice ?? 0) > 0) {
        const casePrice = inventoryItem.productCasePrice ?? -1;
        const unitPrice = inventoryItem.productUnitPrice ?? -1;
        if (casePrice === -1 ||
            unitPrice === -1 ||
            Math.round((casePrice / unitPrice) * 100) / 100 !== actionValue) {
            inventoryItem.invConvFactor = actionValue === 0 ? undefined : actionValue;
        }
        else {
            inventoryItem.invConvFactor = undefined;
        }
    }
    else {
        if (inventoryItem.productConvFactor !== actionValue) {
            inventoryItem.invConvFactor = actionValue === 0 ? undefined : actionValue;
        }
        else {
            inventoryItem.invConvFactor = undefined;
        }
    }
};
/**
 * quantityUpdateFunction: updates the given inventory item's caseOnHand or eachOnHand value.
 *
 * @param inventoryItem
 * @param actionValue
 */
const quantityUpdateFunction = (inventoryItem, actionValue, isCase) => {
    if (isCase) {
        inventoryItem.caseOnHand = actionValue;
    }
    else {
        inventoryItem.eachOnHand = actionValue;
    }
};
/**
 * uomUpdateFunction: updates the given inventory item's Unit of Measure.
 *
 * @param inventoryItem
 * @param actionValue
 */
const uomUpdateFunction = (inventoryItem, actionValue) => {
    inventoryItem.convFactorUnit = actionValue === '' ? undefined : actionValue;
};
/**
 * customUOMUpdateFunction: updates the given inventory item's Custom Unit of Measure.
 *
 * @param inventoryItem
 * @param actionValue
 */
const customUOMUpdateFunction = (inventoryItem, actionValue) => {
    inventoryItem.customConvFactorUnit =
        actionValue === '' ? undefined : actionValue;
    if (inventoryItem.customConvFactorUnit) {
        inventoryItem.convFactorUnit = inventoryItem.customConvFactorUnit;
    }
};
/**
 * uomResetFunction: resets the given inventory item's Unit of Measure to undefined.
 *
 * @param inventoryItem
 * @param ignore
 */
const uomResetFunction = (inventoryItem, ignore) => {
    inventoryItem.convFactorUnit = undefined;
};
/**
 * uomAndConversionUpdateFunction: updates UOM and/or conversion factor for an item
 *
 * @param inventoryItem
 * @param newUOMandConversion
 */
const uomAndConversionUpdateFunction = (inventoryItem, newUOMandConversion) => {
    if (newUOMandConversion.uom && !inventoryItem.productCatchWeightFlag) {
        if ((newUOMandConversion.customUOM && newUOMandConversion.uom === 'Custom') ||
            newUOMandConversion.uom === inventoryItem.customConvFactorUnit) {
            customUOMUpdateFunction(inventoryItem, newUOMandConversion.customUOM ? newUOMandConversion.customUOM : '');
        }
        else if (newUOMandConversion.uom !== 'Custom') {
            uomUpdateFunction(inventoryItem, newUOMandConversion.uom);
        }
    }
    if (newUOMandConversion.conversionValue &&
        inventoryItem.productConvFactor !== newUOMandConversion.conversionValue) {
        inventoryItem.invConvFactor =
            newUOMandConversion.conversionValue === 0
                ? undefined
                : newUOMandConversion.conversionValue;
    }
};
/**
 * operateCloseOnInventory: updates the given inventory item based on the given operation.
 *
 * @param inventory
 * @param groupName
 * @param itemNumber
 * @param operation
 * @param value
 * @param functionalSwitch
 * @returns
 */
const operateClosureOnInventory = (inventory, groupName, itemNumber, operation, value, functionalSwitch) => {
    inventory.groups?.forEach((group) => {
        if (group.groupName === groupName) {
            group.groupItems?.forEach((inventoryItem) => {
                if (inventoryItem.itemNumber === itemNumber) {
                    if (functionalSwitch !== undefined) {
                        operation(inventoryItem, value, functionalSwitch);
                    }
                    else {
                        operation(inventoryItem, value);
                    }
                }
            });
        }
    });
    return inventory;
};
/**
 * operateCloseOnInventoryToMultipleItems: updates the given inventory items based on the given operation.
 *
 * @param inventory
 * @param groupItemMap
 * @param operation
 * @param value
 * @param functionalSwitch
 * @returns
 */
const operateClosureOnInventoryToMultipleItems = (inventory, groupItemMap, operation, value, functionalSwitch) => {
    inventory.groups?.forEach((group) => {
        if (groupItemMap.has(group.groupName)) {
            group.groupItems?.forEach((inventoryItem) => {
                if (groupItemMap.get(group.groupName)?.includes(inventoryItem.itemNumber)) {
                    if (functionalSwitch !== undefined) {
                        operation(inventoryItem, value, functionalSwitch);
                    }
                    else {
                        operation(inventoryItem, value);
                    }
                }
            });
        }
    });
    return inventory;
};
/**
 * generateMutableInventory: generates an inventory with updated groups and items.
 *
 * @param inventoryToCopy
 * @returns
 */
const generateMutableInventory = (inventoryToCopy) => {
    const inventory = { ...inventoryToCopy, groups: [] };
    inventoryToCopy.groups?.forEach((group) => {
        const groupToAdd = { ...group, groupItems: [] };
        group.groupItems?.forEach((item) => {
            const itemToAdd = { ...item };
            groupToAdd.groupItems.push(itemToAdd);
        });
        inventory.groups.push(groupToAdd);
    });
    return inventory;
};
/**
 * updateInventoryStateWithAPIInformation: generates an inventory using the current inventory state values and updating
 * with api values to make sure item and group changes are kept.
 *
 * @param inventoryToCopy
 * @returns
 */
const updateInventoryStateWithAPIInformation = (inventoryFromState, inventoryFromApi) => {
    // eslint-disable-next-line prefer-const
    let stateToReturn = {
        ...inventoryFromState,
    };
    stateToReturn.ecomUpdateDtm = inventoryFromApi.ecomUpdateDtm;
    stateToReturn.ecomUpdateUserId = inventoryFromApi.ecomUpdateUserId;
    stateToReturn.updatedByUser = inventoryFromApi.updatedByUser;
    stateToReturn.inventoryName = inventoryFromApi.inventoryName;
    stateToReturn.deleteInvIndicator = inventoryFromApi.deleteInvIndicator;
    return stateToReturn;
};
/**
 * updateSelection: updates inventory selections that link back to inventory items.
 *
 * @param selection
 * @param selections
 * @returns
 */
const updateSelection = (selection, selections) => {
    const check = selections.filter((value) => value.inventoryId === selection.inventoryId &&
        value.groupName === selection.groupName &&
        value.inventoryItemId === selection.inventoryItemId);
    if (check.length === 0) {
        selections.push(selection);
    }
    else {
        selections[selections.indexOf(check[0])] = selection;
    }
    return selections.filter((select) => select.selection === true);
};
/**
 * selectAllInventoryItemsByGroup: adds all inventory items into the selections array by group.
 *
 * @param inventory
 * @param selectedGroup
 * @returns
 */
const selectAllInventoryItemsByGroup = (inventory, selectedGroup) => {
    const selections = [];
    if (selectedGroup.groupName === 'All') {
        inventory.groups?.forEach((group) => {
            group.groupItems?.forEach((item) => {
                const selection = {
                    inventoryItemId: item?.itemNumber,
                    inventoryId: inventory?.inventoryId,
                    groupName: group.groupName,
                    selection: true,
                    isCatchWeight: item.productCatchWeightFlag,
                };
                selections.push(selection);
            });
        });
    }
    else {
        const filteredGroup = inventory.groups?.filter((group) => group.groupName === selectedGroup.groupName);
        if (filteredGroup && filteredGroup?.length === 1) {
            filteredGroup[0]?.groupItems?.forEach((groupItem) => {
                const selection = {
                    inventoryItemId: groupItem?.itemNumber,
                    inventoryId: inventory?.inventoryId,
                    groupName: filteredGroup[0].groupName,
                    selection: true,
                    isCatchWeight: groupItem.productCatchWeightFlag,
                };
                selections.push(selection);
            });
        }
    }
    return selections;
};
/**
 * mergeItems: merges inventory items for move scenarios
 *
 * @param selection
 * @param selections
 * @returns
 */
const mergeInventoryItems = (itemToCompare, itemToKeep) => {
    if (itemToKeep.caseOnHand && itemToCompare.caseOnHand) {
        itemToKeep.caseOnHand = itemToKeep.caseOnHand + itemToCompare.caseOnHand;
    }
    else if (itemToCompare.caseOnHand) {
        itemToKeep.caseOnHand = itemToCompare.caseOnHand;
    }
    if (itemToKeep.eachOnHand && itemToCompare.eachOnHand) {
        itemToKeep.eachOnHand = itemToKeep.eachOnHand + itemToCompare.eachOnHand;
    }
    else if (itemToCompare.eachOnHand) {
        itemToKeep.eachOnHand = itemToCompare.eachOnHand;
    }
    if (itemToCompare.overrideCasePrice) {
        itemToKeep.overrideCasePrice = itemToCompare.overrideCasePrice;
        itemToKeep.priceOverrideInd = 'Y';
    }
    if (itemToCompare.invConvFactor) {
        itemToKeep.invConvFactor = itemToCompare.invConvFactor;
    }
    if (itemToCompare.convFactorUnit) {
        itemToKeep.convFactorUnit = itemToCompare.convFactorUnit;
    }
    if (itemToCompare.customConvFactorUnit) {
        itemToKeep.customConvFactorUnit = itemToCompare.customConvFactorUnit;
    }
    return itemToKeep;
};
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function inventoryReducer(state, action) {
    return _inventoryReducer(state, action);
}

const selectInventoryState = createFeatureSelector(INVENTORY_KEY);
const selectInventoryById = (inventoryId) => createSelector(selectInventoryState, (inventories) => inventories.entities[inventoryId]);
const selectInventoryOptionPrice = () => createSelector(selectInventoryState, (inventoryState) => inventoryState.inventoryOptions?.priceSystemSelected);
const selectInventoryItemModalOptions = () => createSelector(selectInventoryState, (inventoryState) => inventoryState.inventoryItemModalOptions);
const selectInventoryValidators = () => createSelector(selectInventoryState, (inventoryState) => inventoryState.inventoryOptions.validators);
const selectInventoryOnline = () => createSelector(selectInventoryState, (inventoryState) => inventoryState.isOnline);
const selectAllInventoriesSearchKey = () => createSelector(selectInventoryState, (inventoryState) => inventoryState.allInventoriesSearchKey);
const { selectIds: selectInventoryIds, selectEntities: selectInventoryEntities, selectAll: selectAllInventories, selectTotal: selectTotalInventories, } = inventoryAdapter.getSelectors(selectInventoryState);
const selectInventoryOptions = () => createSelector(selectInventoryState, (inventoryState) => inventoryState.inventoryOptions);
const selectInventoriesWithCounts = createSelector(inventoryAdapter.getSelectors(selectInventoryState).selectAll, (inventoryStates) => inventoryStates?.map((inventoryState) => {
    let count = 0;
    if (inventoryState?.groups) {
        for (const group of inventoryState.groups) {
            count += group?.groupItems?.length ?? 0;
        }
    }
    return { ...inventoryState, count };
}));
const selectPendingItemUpdateState = createSelector(selectInventoryState, (inventoryState) => inventoryState.pendingItemUpdates);
const selectMissedItemUpdates = createSelector(selectInventoryState, (inventoryState) => inventoryState.missedItemUpdates);

/* eslint-disable max-len */
/* eslint-disable no-shadow */
var InventoryTracingConstants;
(function (InventoryTracingConstants) {
    // Trace Contexts
    InventoryTracingConstants["inventory"] = "inventory";
    // Events
    InventoryTracingConstants["createInventoryEvent"] = "Create Inventory";
    InventoryTracingConstants["deleteInventoryEvent"] = "Delete Inventory";
    InventoryTracingConstants["deleteInventoryGroupEvent"] = "Delete Inventory Group";
    InventoryTracingConstants["deleteInventoryItemsEvent"] = "Delete Inventory Items";
    InventoryTracingConstants["priceOverrideEvent"] = "Inventory Price Override";
    InventoryTracingConstants["displayInventoryEvent"] = "Display Inventory";
    InventoryTracingConstants["pdpValueEvent"] = "Inventory pdp value change";
    InventoryTracingConstants["addUSFProductsEvent"] = "Add USF products to inventory";
    InventoryTracingConstants["inventoryCountsEvent"] = "Count Inventory Items";
    InventoryTracingConstants["copyInventoryEvent"] = "Copy Inventory";
    InventoryTracingConstants["updateItemsEvent"] = "Update Inventory Item";
    // Span Names
    InventoryTracingConstants["createInventorySpan"] = "Create Inventory";
    InventoryTracingConstants["deleteInventorySpan"] = "Delete Inventory";
    InventoryTracingConstants["deleteInventoryGroupSpan"] = "Delete Inventory Group";
    InventoryTracingConstants["priceOverrideSpan"] = "Inventory Price Override";
    InventoryTracingConstants["displayInventorySpan"] = "Display Inventory";
    InventoryTracingConstants["pdpValueSpan"] = "Inventory pdp value change";
    InventoryTracingConstants["addUSFProductsSpan"] = "Add USF products to inventory";
    InventoryTracingConstants["inventoryCountsSpan"] = "Count Inventory Items";
    InventoryTracingConstants["copyInventorySpan"] = "Copy Inventory";
    InventoryTracingConstants["updateItemsSpan"] = "Update Inventory Item";
    // values
    InventoryTracingConstants["pastInventory"] = "Past Inventory";
    InventoryTracingConstants["shoppingList"] = "Shopping List";
    InventoryTracingConstants["pastPurchhase"] = "Past Purchase";
    InventoryTracingConstants["addItems"] = "Add Items Manually";
    // Descriptions
    InventoryTracingConstants["eventSuccess"] = " Success";
    InventoryTracingConstants["eventFailure"] = " Failure";
    InventoryTracingConstants["spanSuccess"] = " Success";
    InventoryTracingConstants["spanFailure"] = " Failure";
    InventoryTracingConstants["deleteGroupMoveProducts"] = "Delete group and move all items to unassigned group";
    InventoryTracingConstants["deleteGroupDeleteProducts"] = "Delete group and delete all products";
})(InventoryTracingConstants || (InventoryTracingConstants = {}));

/**
 * create Inventory
 *
 * @param tracking
 * @returns Data Layer needed for tracking creating inventory
 */
const createInventoryTransformer = (tracking) => ({
    ...baseTransform(tracking),
    inventory: {
        inventoryName: tracking.data.attributes.inventoryName,
        inventoryId: tracking.data.attributes.inventoryId,
        createFrom: tracking.data.attributes.createFrom,
    },
});
/**
 * delete Inventory
 *
 * @param tracking
 * @returns Data Layer needed for tracking creating inventory
 */
const deleteInventoryTransformer = (tracking) => ({
    ...baseInventoryTransform(tracking),
});
/**
 * updated price override
 *
 * @param tracking
 * @returns Data Layer needed for tracking price override
 */
const priceOverrideTransformer = (tracking) => ({
    ...baseInventoryTransform(tracking),
    products: tracking.data.attributes.products,
});
/**
 * display inventory
 *
 * @param tracking
 * @returns Data Layer needed for tracking display inventory
 */
const displayInventoryTransformer = (tracking) => ({
    ...baseInventoryTransform(tracking),
});
/**
 * delete Inventory group success
 *
 * @param tracking
 * @returns Data Layer needed for tracking delete inventory group
 */
const deleteGroupMoveItems = (tracking) => ({
    ...baseInventoryTransform(tracking),
});
/**
 * delete Inventory group fail
 *
 * @param tracking
 * @returns Data Layer needed for tracking delete inventory group
 */
const deleteGroupDeleteItems = (tracking) => ({
    ...baseInventoryTransform(tracking),
    products: tracking.data.attributes.products,
});
const baseInventoryTransform = (tracking) => ({
    ...baseTransform(tracking),
    inventory: {
        inventoryName: tracking.data.attributes.inventoryName,
        inventoryId: tracking.data.attributes.inventoryId,
    },
});
const baseTransform = (tracking) => {
    let result = {
        event: tracking.data.attributes.event,
        spanName: tracking.data.attributes.spanName,
        traceContext: tracking.data.traceContext,
        isStartOfTrace: tracking.data.isStartOfTrace,
        isEndOfTrace: tracking.data.isEndOfTrace,
    };
    if (tracking.data.attributes?.error) {
        result = {
            ...result,
            http: {
                statusCode: tracking.data.attributes.error.status,
                statusText: tracking.data.attributes.error.message,
            },
        };
    }
    return result;
};
const createSuccessTrackingObject = (tracking, inventoryId) => ({
    tracing: {
        data: {
            traceContext: tracking.tracing?.data.traceContext ?? '',
            isEndOfTrace: true,
            isStartOfTrace: false,
            attributes: {
                ...tracking.tracing?.data.attributes,
                inventoryId,
                event: tracking.tracing?.data.attributes?.event +
                    InventoryTracingConstants.eventSuccess,
                spanName: tracking.tracing?.data.attributes?.spanName +
                    InventoryTracingConstants.spanSuccess,
            },
        },
        transformFunc: tracking.tracing?.transformFunc,
    },
});
const createFailureTrackingObject = (tracking, error) => ({
    tracing: {
        data: {
            traceContext: tracking.tracing?.data.traceContext ?? '',
            isEndOfTrace: true,
            isStartOfTrace: false,
            attributes: {
                ...tracking.tracing?.data.attributes,
                event: tracking.tracing?.data.attributes?.event +
                    InventoryTracingConstants.eventFailure,
                spanName: tracking.tracing?.data.attributes?.spanName +
                    InventoryTracingConstants.spanFailure,
                error: {
                    status: error.status,
                    message: error.message,
                },
            },
        },
        transformFunc: tracking.tracing?.transformFunc,
    },
});
const updateInventoryQuantity = (tracking) => ({
    ...baseInventoryTransform(tracking),
    product: tracking.data.attributes.product,
});
const copyInventoryTransform = (tracking) => ({
    ...baseTransform(tracking),
    inventory: {
        newInventoryName: tracking.data.attributes.newInventoryName,
        inventoryId: tracking.data.attributes.pastInventoryId,
        newInventoryId: tracking.data.attributes.inventoryId,
    },
});
const updateInventoryItemTransform = (tracking) => ({
    traceContext: tracking.data.traceContext,
    isStartOfTrace: tracking.data.isStartOfTrace,
    isEndOfTrace: tracking.data.isEndOfTrace,
    ...tracking.data.attributes,
});

/**
 * create Inventory
 *
 * @param tracking
 * @returns Data Layer needed for tracking create inventory
 */
const createInventoryAnalyticsTransformer = (tracking) => ({
    ...baseInventoryAnalyticsTransform(tracking),
    inventory: {
        inventoryId: tracking.data.inventoryId,
        createSource: tracking.data.createSource,
    },
});
/**
 * page loaded Inventory Worksheet
 *
 * @param tracking
 * @returns Data Layer needed for tracking Inventory worksheet loaded
 */
const pageLoadedTransformer = (tracking) => ({
    ...baseInventoryAnalyticsTransform(tracking),
    page: {
        pageName: tracking.data.pageName,
    },
});
const clickButtonTransformer = (tracking) => ({
    ...baseInventoryAnalyticsTransform(tracking),
    button: {
        buttonName: tracking.data.buttonName,
    },
});
const downloadInventoryTransformer = (tracking) => ({
    ...baseInventoryAnalyticsTransform(tracking),
    file: {
        type: tracking.data.fileType,
    },
    inventory: {
        inventoryID: tracking.data.inventoryId,
    },
});
const downloadCostReportInventoryTransformer = (tracking) => ({
    ...baseInventoryAnalyticsTransform(tracking),
    file: {
        type: tracking.data.fileType,
        fileCount: tracking.data.fileCount,
    },
});
const modalAnalyticsTransformer = (tracking) => ({
    ...baseInventoryAnalyticsTransform(tracking),
    modal: {
        name: tracking.data.modalName,
    },
});
const baseInventoryAnalyticsTransform = (tracking) => ({
    capability: tracking.data.capability,
    event: tracking.data.event,
    inventory: {
        inventoryId: tracking.data.inventoryId,
    },
});

const ANALYTICS_ACTIONS = {
    trackCreateInventory: createAction('[NgRxInventory - Analytics] - Track Inventory Creation', props()),
    trackInventoryWorksheetPageLoad: createAction('[NgRxInventory - Analytics] - Track Inventory Worksheet Load', props()),
    trackClickUpdateAllPricesButton: createAction('[NgRxInventory - Analytics] - Track click on Update All Prices button', props()),
    trackAddProductPageLoaded: createAction('[NgRxInventory - Analytics] - Track page load of add product to inventory page', props()),
    trackAddNonUsfProductPageLoaded: createAction('[NgRxInventory - Analytics] - Track page load of add non-usf product to inventory page', props()),
    trackUpdateProductUnitByCase: createAction('[NgRxInventory - Analytics] - Track users updating product unit by case', props()),
    trackDownloadInventoryWorksheet: createAction('[NgRxInventory - Analytics] - Track inventory worksheet download', props()),
    trackDownloadInventoryCostReport: createAction('[NgRxInventory - Analytics] - Track inventory cost report download', props()),
    trackUpdatingInventoryPrice: createAction('[NgRxInventory - Analytics] - Track users updating product inventory price', props()),
    trackCreateInventoryPageLoaded: createAction('[NgRxInventory - Analytics] - Track page load of inventory creation', props()),
    trackAllInventoriesPageLoaded: createAction('[NgRxInventory - Analytics] - Track page load of All inventories', props()),
    trackInventoryHomePageLoaded: createAction('[NgRxInventory - Analytics] - Track page load of Inventory Home', props()),
    trackFoodCostCalculatorPageLoaded: createAction('[NgRxInventory - Analytics] - Track page load of Food Cost Calculator', props()),
    trackCreateNewReportModal: createAction('[NgRxInventory - Analytics] - Track Modal Open for Create New Report', props()),
    trackCreateNewFoodCostReport: createAction('[NgRxInventory - Analytics] - Track User Creating New Report under Food Cost Calculator', props()),
};

var InventoryAnalyticsConstants;
(function (InventoryAnalyticsConstants) {
    //Capabilities
    InventoryAnalyticsConstants["capability"] = "inventory";
    InventoryAnalyticsConstants["listCapability"] = "lists";
    //Events
    InventoryAnalyticsConstants["completeInventory"] = "complete inventory";
    InventoryAnalyticsConstants["updateUOM"] = "change inventory uom";
    InventoryAnalyticsConstants["pageLoaded"] = "page loaded";
    InventoryAnalyticsConstants["createInventory"] = "create inventory";
    InventoryAnalyticsConstants["buttonClick"] = "button click";
    InventoryAnalyticsConstants["updateUnitByCase"] = "change inventory unit by case";
    InventoryAnalyticsConstants["downloadEvent"] = "share print download";
    InventoryAnalyticsConstants["changeInventoryPrice"] = "change inventory value";
    InventoryAnalyticsConstants["modalOpen"] = "modal open";
    InventoryAnalyticsConstants["createNewFoodCostReportEvent"] = "create new food cost report";
    //Page names
    InventoryAnalyticsConstants["inventoryWorksheet"] = "us foods:r4:inventory worksheet";
    InventoryAnalyticsConstants["addUsfProduct"] = "us foods:r4:add to inventory:usf product";
    InventoryAnalyticsConstants["addNonUsfProduct"] = "us foods:r4:add to inventory:non-usf product";
    InventoryAnalyticsConstants["createInventoryPage"] = "us foods:r4:create inventory";
    InventoryAnalyticsConstants["allInventoriesPage"] = "us foods:r4:all inventories";
    InventoryAnalyticsConstants["inventoryHome"] = "us foods:r4:inventory";
    InventoryAnalyticsConstants["foodCostCalculatorPage"] = "us foods:r4:food cost calculator";
    //createSource
    InventoryAnalyticsConstants["manual"] = "manual";
    InventoryAnalyticsConstants["shoppingList"] = "shopping list";
    InventoryAnalyticsConstants["pastPurchase"] = "past purchase";
    InventoryAnalyticsConstants["pastInventory"] = "past inventory";
    InventoryAnalyticsConstants["updateAllPrices"] = "update all prices";
    InventoryAnalyticsConstants["fileTypeCsv"] = "download:inventory:csv";
    InventoryAnalyticsConstants["fileTypePdf"] = "download:inventory:pdf";
    //Modal names
    InventoryAnalyticsConstants["createNewFoodCostReport"] = "create new food cost report";
})(InventoryAnalyticsConstants || (InventoryAnalyticsConstants = {}));

const NGRX_INVENTORY_LIB_CONFIG = new InjectionToken('NGRX_INVENTORY_LIB_CONFIG');

/* eslint-disable @typescript-eslint/no-shadow */
class InventoryHttpClientService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    get(path, queryParams) {
        let url = `${this.config.inventoryApiUrl}/${path}`;
        if (queryParams) {
            queryParams.forEach((val, key) => {
                url += url.includes('?') ? `&${key}=${val}` : `?${key}=${val}`;
            });
        }
        return this.http.get(url).pipe(catchError(this.handleError));
    }
    post(path, body) {
        const url = `${this.config.inventoryApiUrl}/${path}`;
        return this.http.post(url, body).pipe(catchError(this.handleError));
    }
    put(path, body) {
        const url = `${this.config.inventoryApiUrl}/${path}`;
        return this.http.put(url, body).pipe(catchError(this.handleError));
    }
    delete(path, body) {
        const url = `${this.config.inventoryApiUrl}/${path}`;
        return this.http.delete(url, body).pipe(catchError(this.handleError));
    }
    handleError(error) {
        return throwError(error);
    }
}
InventoryHttpClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryHttpClientService, deps: [{ token: NGRX_INVENTORY_LIB_CONFIG }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
InventoryHttpClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryHttpClientService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryHttpClientService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_INVENTORY_LIB_CONFIG]
                }] }, { type: i1.HttpClient }]; } });

/* eslint-disable @typescript-eslint/no-shadow */
class InventoryMigrationHttpClientService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    get(path, queryParams) {
        let url = `${this.config.inventoryMigrationApiUrl}/${path}`;
        if (queryParams) {
            queryParams.forEach((val, key) => {
                url += url.includes('?') ? `&${key}=${val}` : `?${key}=${val}`;
            });
        }
        return this.http.get(url).pipe(catchError(this.handleError));
    }
    post(path, body) {
        const url = `${this.config.inventoryMigrationApiUrl}/${path}`;
        return this.http.post(url, body).pipe(catchError(this.handleError));
    }
    put(path, body) {
        const url = `${this.config.inventoryMigrationApiUrl}/${path}`;
        return this.http.put(url, body).pipe(catchError(this.handleError));
    }
    delete(path, body) {
        const url = `${this.config.inventoryMigrationApiUrl}/${path}`;
        return this.http.delete(url, body).pipe(catchError(this.handleError));
    }
    handleError(error) {
        return throwError(error);
    }
}
InventoryMigrationHttpClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryMigrationHttpClientService, deps: [{ token: NGRX_INVENTORY_LIB_CONFIG }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
InventoryMigrationHttpClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryMigrationHttpClientService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryMigrationHttpClientService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_INVENTORY_LIB_CONFIG]
                }] }, { type: i1.HttpClient }]; } });

class InventoryEffects {
    constructor(actions$, httpService, httpMigrationService, store, panAppState) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.httpMigrationService = httpMigrationService;
        this.store = store;
        this.panAppState = panAppState;
        this.multiUserInventoryFlag$ = this.panAppState.feature$([
            'split_dynamic_inventory_multi_user',
        ]);
        /**
         * @remarks
         * Responsible for making api call to fetch Inventory
         */
        this.loadInventory$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ACTIONS.load), concatMap((action) => this.httpService.get(InventoryEndpoints.inventory).pipe(map((inventories) => INVENTORY_ACTIONS.loadSuccess({
            inventories,
        })), catchError((error) => of(INVENTORY_ACTIONS.loadFail({ error })))))));
        /**
         * @remarks
         * Responsible for making api call to fetch Inventory with departments
         */
        this.loadInventoryWithDepartments$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ACTIONS.loadWithAllDepartments), concatMap((action) => this.httpService
            .get(InventoryEndpoints.allDepartments)
            .pipe(map((inventories) => INVENTORY_ACTIONS.loadSuccess({
            inventories,
        })), catchError((error) => of(INVENTORY_ACTIONS.loadFail({ error })))))));
        /**
         * @remarks
         * Responsible for making api call to create Inventory
         */
        this.createInventory$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ACTIONS.create), switchMap((action) => this.httpService
            .post(InventoryEndpoints.inventory, action.inventory)
            .pipe(switchMap((inventory) => [
            INVENTORY_ACTIONS.createSuccess({
                inventory,
                productsNotFound: action.productsNotFound,
                tracking: createSuccessTrackingObject(action.tracking ?? {}, inventory.inventoryId),
                copiedInvName: action.copiedInvName,
            }),
            ANALYTICS_ACTIONS.trackCreateInventory({
                tracking: {
                    analytics: {
                        data: {
                            capability: InventoryAnalyticsConstants.capability,
                            event: InventoryAnalyticsConstants.createInventory,
                            inventoryId: inventory.inventoryId,
                            createSource: action.createSource,
                        },
                        transformFunc: createInventoryAnalyticsTransformer,
                    },
                },
            }),
        ]), catchError((error) => of(INVENTORY_ACTIONS.createFail({
            error,
            tracking: createFailureTrackingObject(action.tracking ?? {}, error),
            copiedInvName: action.copiedInvName,
        })))))));
        /**
         * @remarks
         * Responsible for making api call to update Inventory
         */
        this.updateInventory$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ACTIONS.editName, INVENTORY_ITEM_ACTIONS.resetUOM, INVENTORY_ITEM_ACTIONS.updateUOM, INVENTORY_ITEM_ACTIONS.saveInventoryItemModalOptions, INVENTORY_ITEM_ACTIONS.updateConversionFactor, INVENTORY_ITEM_ACTIONS.updateCustomConversionFactorUnit, INVENTORY_ITEM_ACTIONS.update, INVENTORY_GROUP_ACTIONS.update, INVENTORY_GROUP_ACTIONS.move), withLatestFrom(this.store.select(selectInventoryState)), filter(([action, state]) => state.entities[action.inventoryId] !== undefined), switchMap(([action, state]) => this.httpService
            .put(InventoryEndpoints.inventory, state.entities[action.inventoryId])
            .pipe(map((inventory) => INVENTORY_ACTIONS.updateSuccess({
            inventory,
            inventoryId: inventory.inventoryId,
            tracking: action?.tracking
                ? {
                    tracing: {
                        data: {
                            traceContext: InventoryTracingConstants.inventory,
                            isStartOfTrace: false,
                            isEndOfTrace: true,
                            attributes: {
                                ...action?.tracking?.tracing?.data?.attributes,
                                event: action?.tracking?.tracing?.data?.attributes
                                    ?.event,
                                spanName: action?.tracking?.tracing?.data?.attributes
                                    ?.spanName +
                                    InventoryTracingConstants.spanSuccess,
                            },
                        },
                        transformFunc: action?.tracking?.tracing?.transformFunc,
                    },
                }
                : undefined,
        })), catchError((error) => of(INVENTORY_ACTIONS.updateFail({
            error,
            tracking: action?.tracking
                ? {
                    tracing: {
                        data: {
                            traceContext: InventoryTracingConstants.inventory,
                            isStartOfTrace: false,
                            isEndOfTrace: true,
                            attributes: {
                                ...action.tracking?.tracing?.data.attributes,
                                event: action.tracking?.tracing?.data?.attributes
                                    ?.event,
                                spanName: action.tracking?.tracing?.data?.attributes
                                    ?.spanName +
                                    InventoryTracingConstants.spanFailure,
                            },
                        },
                        transformFunc: action?.tracking?.tracing?.transformFunc,
                    },
                }
                : undefined,
        })))))));
        /**
         * @remarks
         * Responsible for making api call to update Inventory quantity
         */
        this.updateInventoryItemQuantity$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ITEM_ACTIONS.updateQuantity), withLatestFrom(this.store.select(selectInventoryState)), filter(([action, state]) => state.entities[action.inventoryId] !== undefined), switchMap(([action, state]) => this.httpService
            .put(InventoryEndpoints.inventory, state.entities[action.inventoryId])
            .pipe(map((inventory) => INVENTORY_ITEM_ACTIONS.updateQuantitySuccess({
            inventory,
            inventoryId: inventory.inventoryId,
            tracking: action?.tracking
                ? {
                    tracing: {
                        data: {
                            traceContext: InventoryTracingConstants.inventory,
                            isStartOfTrace: false,
                            isEndOfTrace: true,
                            attributes: {
                                ...action?.tracking?.tracing?.data?.attributes,
                                event: action?.tracking?.tracing?.data?.attributes
                                    ?.event,
                                spanName: action?.tracking?.tracing?.data?.attributes
                                    ?.spanName +
                                    InventoryTracingConstants.spanSuccess,
                            },
                        },
                        transformFunc: action?.tracking?.tracing?.transformFunc,
                    },
                }
                : undefined,
        })), catchError((error) => of(INVENTORY_ITEM_ACTIONS.updateQuantityFail({
            error,
            tracking: action?.tracking
                ? {
                    tracing: {
                        data: {
                            traceContext: InventoryTracingConstants.inventory,
                            isStartOfTrace: false,
                            isEndOfTrace: true,
                            attributes: {
                                ...action.tracking?.tracing?.data.attributes,
                                event: action.tracking?.tracing?.data?.attributes
                                    ?.event,
                                spanName: action.tracking?.tracing?.data?.attributes
                                    ?.spanName +
                                    InventoryTracingConstants.spanFailure,
                            },
                        },
                        transformFunc: action?.tracking?.tracing?.transformFunc,
                    },
                }
                : undefined,
        })))))));
        /**
         * @remarks
         * Responsible for making api call to update Inventory
         */
        this.updateInventoryWithTracing$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ITEM_ACTIONS.updatePriceOverride), withLatestFrom(this.store.select(selectInventoryState)), filter(([action, state]) => state.entities[action.inventoryId] !== undefined), switchMap(([action, state]) => this.httpService
            .put(InventoryEndpoints.inventory, state.entities[action.inventoryId])
            .pipe(map((inventory) => INVENTORY_ACTIONS.updateWithTracingSuccess({
            inventory,
            inventoryId: inventory.inventoryId,
            tracking: action?.tracking
                ? {
                    tracing: {
                        data: {
                            traceContext: InventoryTracingConstants.inventory,
                            isStartOfTrace: false,
                            isEndOfTrace: true,
                            attributes: {
                                ...action?.tracking?.tracing?.data?.attributes,
                                event: action?.tracking?.tracing?.data?.attributes
                                    ?.event +
                                    InventoryTracingConstants.eventSuccess,
                                spanName: action?.tracking?.tracing?.data?.attributes
                                    ?.spanName +
                                    InventoryTracingConstants.spanSuccess,
                            },
                        },
                        transformFunc: action?.tracking?.tracing?.transformFunc,
                    },
                }
                : undefined,
        })), catchError((error) => of(INVENTORY_ACTIONS.updateWithTracingFail({
            error,
            tracking: action?.tracking
                ? {
                    tracing: {
                        data: {
                            traceContext: InventoryTracingConstants.inventory,
                            isStartOfTrace: false,
                            isEndOfTrace: true,
                            attributes: {
                                ...action.tracking?.tracing?.data.attributes,
                                event: action.tracking?.tracing?.data?.attributes
                                    ?.event +
                                    InventoryTracingConstants.eventFailure,
                                spanName: action.tracking?.tracing?.data?.attributes
                                    ?.spanName +
                                    InventoryTracingConstants.spanFailure,
                            },
                        },
                        transformFunc: action?.tracking?.tracing?.transformFunc,
                    },
                }
                : undefined,
        })))))));
        this.updateInventory2$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ACTIONS.update), withLatestFrom(this.store.select(selectInventoryState)), filter(([action, state]) => state.entities[action.inventory.inventoryId] !== undefined), switchMap(([action, state]) => this.httpService
            .put(InventoryEndpoints.inventory, state.entities[action.inventory.inventoryId])
            .pipe(map((inventory) => INVENTORY_ACTIONS.updateSuccess({
            inventory,
        })), catchError((error) => of(INVENTORY_ACTIONS.updateFail({ error })))))));
        this.completeInventory$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ACTIONS.complete), withLatestFrom(this.store.select(selectInventoryState)), filter(([action, state]) => state.entities[action.inventoryId] !== undefined), switchMap(([action, state]) => this.httpService
            .put(InventoryEndpoints.inventory, state.entities[action.inventoryId])
            .pipe(map((inventory) => INVENTORY_ACTIONS.completeSuccess({
            inventory,
        })), catchError((error) => of(INVENTORY_ACTIONS.completeFail({ error })))))));
        this.moveInventoryItem$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ITEM_ACTIONS.moveInventoryItemByLineNumber), withLatestFrom(this.store.select(selectInventoryState)), filter(([action, state]) => state.entities[action.inventoryId] !== undefined), switchMap(([action, state]) => {
            if (state.inventoryOptions.allDuplicates) {
                return [
                    INVENTORY_ACTIONS.resetAllDuplicatesFlag(),
                    INVENTORY_ITEM_ACTIONS.moveInventoryItemDuplicateFailure(),
                ];
            }
            return this.httpService
                .put(InventoryEndpoints.inventory, state.entities[action.inventoryId])
                .pipe(map((inventory) => INVENTORY_ACTIONS.updateSuccess({
                inventory,
            })), catchError((error) => of(INVENTORY_ACTIONS.updateWithToastMessageFail({
                error,
                messageForToast: action?.messageForToast
                    ? action?.messageForToast + 'Fail'
                    : undefined,
            }))));
        })));
        this.updateWithToastMessage$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ITEM_ACTIONS.moveInventoryItemsIntoGroup, INVENTORY_ITEM_ACTIONS.copyInventoryItemsIntoGroup, INVENTORY_ITEM_ACTIONS.addInventoryItemsIntoGroup, INVENTORY_ITEM_ACTIONS.updateUOMAndConversionFactor, INVENTORY_ITEM_ACTIONS.deleteInventoryItemsInGroup, INVENTORY_ITEM_ACTIONS.updateCustomConversionFactorUnit), withLatestFrom(this.store.select(selectInventoryState)), filter(([action, state]) => state.entities[action.inventoryId] !== undefined), switchMap(([action, state]) => this.httpService
            .put(InventoryEndpoints.inventory, state.entities[action.inventoryId])
            .pipe(map((inventory) => INVENTORY_ACTIONS.updateWithToastMessageSuccess({
            inventoryId: action.inventoryId,
            inventory,
            messageForToast: action?.messageForToast
                ? action?.messageForToast + 'Success'
                : undefined,
            allDuplicates: state?.inventoryOptions?.allDuplicates,
            productsAdded: state?.inventoryOptions?.productsAdded,
            tracking: action?.tracking
                ? {
                    tracing: {
                        data: {
                            traceContext: InventoryTracingConstants.inventory,
                            isStartOfTrace: false,
                            isEndOfTrace: true,
                            attributes: {
                                ...action?.tracking?.tracing?.data?.attributes,
                                event: action?.tracking?.tracing?.data?.attributes
                                    ?.event + InventoryTracingConstants.spanSuccess,
                                spanName: action?.tracking?.tracing?.data?.attributes
                                    ?.spanName +
                                    InventoryTracingConstants.spanSuccess,
                            },
                        },
                        transformFunc: action?.tracking?.tracing?.transformFunc,
                    },
                }
                : undefined,
        })), catchError((error) => of(INVENTORY_ACTIONS.updateWithToastMessageFail({
            error,
            messageForToast: action?.messageForToast
                ? action?.messageForToast + 'Fail'
                : undefined,
            tracking: action?.tracking
                ? {
                    tracing: {
                        data: {
                            traceContext: InventoryTracingConstants.inventory,
                            isStartOfTrace: false,
                            isEndOfTrace: true,
                            attributes: {
                                ...action.tracking?.tracing?.data.attributes,
                                event: action.tracking?.tracing?.data?.attributes
                                    ?.event +
                                    InventoryTracingConstants.spanFailure,
                                spanName: action.tracking?.tracing?.data?.attributes
                                    ?.spanName +
                                    InventoryTracingConstants.spanFailure,
                            },
                        },
                        transformFunc: action?.tracking?.tracing?.transformFunc,
                    },
                }
                : undefined,
        })))))));
        this.updateWithToastMessageAndParams$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_GROUP_ACTIONS.delete, INVENTORY_GROUP_ACTIONS.deleteAndMoveItems), withLatestFrom(this.store.select(selectInventoryState)), filter(([action, state]) => state.entities[action.inventoryId] !== undefined), switchMap(([action, state]) => this.httpService
            .put(InventoryEndpoints.inventory, state.entities[action.inventoryId])
            .pipe(map((inventory) => INVENTORY_ACTIONS.updateWithToastMessageSuccess({
            inventoryId: action.inventoryId,
            inventory,
            messageForToast: action.messageForToast + 'Success',
            toastInputParams: action.toastInputParams,
            tracking: {
                tracing: {
                    data: {
                        traceContext: InventoryTracingConstants.inventory,
                        isStartOfTrace: false,
                        isEndOfTrace: true,
                        attributes: {
                            ...action.tracking?.tracing?.data?.attributes,
                            event: action.tracking?.tracing?.data?.attributes?.event +
                                InventoryTracingConstants.spanSuccess,
                            spanName: action.tracking?.tracing?.data?.attributes?.spanName +
                                InventoryTracingConstants.spanSuccess,
                        },
                    },
                    transformFunc: action.tracking?.tracing?.transformFunc,
                },
            },
        })), catchError((error) => of(INVENTORY_ACTIONS.updateWithToastMessageFail({
            error,
            messageForToast: action.messageForToast + 'Fail',
            tracking: {
                tracing: {
                    data: {
                        traceContext: InventoryTracingConstants.inventory,
                        isStartOfTrace: false,
                        isEndOfTrace: true,
                        attributes: {
                            ...action.tracking?.tracing?.data.attributes,
                            event: action.tracking?.tracing?.data?.attributes?.event +
                                InventoryTracingConstants.spanFailure,
                            spanName: action.tracking?.tracing?.data?.attributes
                                ?.spanName +
                                InventoryTracingConstants.spanFailure,
                        },
                    },
                    transformFunc: deleteGroupMoveItems,
                },
            },
        })))))));
        this.createInventoryGroup$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_GROUP_ACTIONS.create), withLatestFrom(this.store.select(selectInventoryState)), filter(([action, state]) => state.entities[action.inventoryId] !== undefined), switchMap(([action, state]) => this.httpService
            .put(InventoryEndpoints.inventory, state.entities[action.inventoryId])
            .pipe(map((inventory) => INVENTORY_GROUP_ACTIONS.createSuccess({
            inventory,
        })), catchError((error) => of(INVENTORY_GROUP_ACTIONS.createFail({ error })))))));
        this.backOnline$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ACTIONS.exitOffline), withLatestFrom(this.store.select(selectInventoryState), this.multiUserInventoryFlag$), filter(([action, state, multiUserInventoryFlag]) => state.entities[action.inventoryId ?? ''] !== undefined &&
            !multiUserInventoryFlag), switchMap(([action, state]) => this.httpService
            .put(InventoryEndpoints.inventory, state.entities[action.inventoryId ?? ''])
            .pipe(map((inventory) => INVENTORY_ACTIONS.updateSuccess({
            inventory,
            inventoryId: action.inventoryId,
        })), catchError((error) => of(INVENTORY_ACTIONS.updateFail({ error })))))));
        this.deleteInventory$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ACTIONS.delete), withLatestFrom(this.store.select(selectInventoryState)), filter(([action, state]) => state.entities[action.inventoryId] !== undefined), switchMap(([action, state]) => {
            const queryParams = new HttpParams().set('inventoryId', action.inventoryId);
            return this.httpService
                .delete(InventoryEndpoints.inventory, {
                params: queryParams,
            })
                .pipe(map(() => INVENTORY_ACTIONS.deleteSuccess({
                inventoryId: action.inventoryId,
                inventoryName: action.inventoryName,
                tracking: createSuccessTrackingObject(action.tracking ?? {}, action.inventoryId),
            })), catchError((error) => of(INVENTORY_ACTIONS.deleteFail({
                error,
                tracking: createFailureTrackingObject(action.tracking ?? {}, error),
            }))));
        })));
        /**
         * @remarks
         * Responsible for making api call to fetch inventory migration data
         */
        this.loadCustomerMigration$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ACTIONS.loadCustomerMigration), concatMap((action) => this.httpService
            .get(InventoryEndpoints.customerMigration)
            .pipe(map((customerMigrationData) => INVENTORY_ACTIONS.loadCustomerMigrationSuccess({
            customerMigration: customerMigrationData,
        })), catchError((error) => of(INVENTORY_ACTIONS.loadCustomerMigrationFail({ error })))))));
        /**
         * @remarks
         * Responsible for making api call to start the inventory migration, then
         * dispatches success or fail action
         */
        this.initiateMigration$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ACTIONS.initiateMigration), concatMap((action) => this.httpMigrationService
            .post(InventoryEndpoints.inventoryMigration, action.departments)
            .pipe(concatMap(() => [
            INVENTORY_ACTIONS.migrationSuccess(),
            INVENTORY_ACTIONS.loadCustomerMigration(),
            INVENTORY_ACTIONS.load(),
        ]), catchError((error) => [
            INVENTORY_ACTIONS.migrationFail({ error }),
            INVENTORY_ACTIONS.loadCustomerMigration(),
        ])))));
        /**
         * @remarks
         * Responsible for making api call for Extended Cost Report
         */
        this.extendedCostReport$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ACTIONS.downloadExtendedCostReport), switchMap((action) => this.httpService
            .post(InventoryEndpoints.extendedCostReport +
            '?startDate=' +
            action.dates[0] +
            '&endDate=' +
            action.dates[action.dates.length - 1], {
            customerAndDivisionInfo: action.customerDivisionPair,
            timeZoneOffset: action.timeZoneOffset,
        })
            .pipe(switchMap((response) => [
            INVENTORY_ACTIONS.downloadExtendedCostReportSuccess({
                response: response.value,
                startDate: action.dates[0],
                endDate: action.dates[action.dates.length - 1],
            }),
        ]), catchError((error) => of(INVENTORY_ACTIONS.downloadExtendedCostReportFail({
            error,
        })))))));
        /**
         * @remarks
         * Responsible for making api call for Updating non usf values
         */
        this.nonUSFValueUpdate$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ITEM_ACTIONS.nonUSFValueUpdate), switchMap((action) => this.httpService
            .put(InventoryEndpoints.updateItemValues, action.items.map((item) => ({
            productNumber: item.nonUsfProductNumber,
            productPackSizeLabel: item.productPackSizeLabel,
            productUnitOfMeasure: item.productUnitOfMeasure,
            productConvFactor: item.productConvFactor,
            productCasePrice: item.productCasePrice,
        })))
            .pipe(switchMap((response) => [
            INVENTORY_ITEM_ACTIONS.nonUSFValueUpdateSuccess({
                inventoryIds: response.updatedInventoryIds,
                timeStamp: response.updateTimestamp,
                items: action.items,
            }),
        ]), catchError((error) => of(INVENTORY_ITEM_ACTIONS.nonUSFValueUpdateFail({
            error,
        })))))));
    }
}
InventoryEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryEffects, deps: [{ token: i1$1.Actions }, { token: InventoryHttpClientService }, { token: InventoryMigrationHttpClientService }, { token: i1$2.Store }, { token: i2.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
InventoryEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Actions }, { type: InventoryHttpClientService }, { type: InventoryMigrationHttpClientService }, { type: i1$2.Store }, { type: i2.PanAppState }]; } });

const INVENTORY_UI_CONSTANTS_ACTIONS = {
    load: createAction('[NgRxInventory - UiConstants] Load Inventory UI Constants from Domain API'),
    loadSuccess: createAction('[NgRxInventory - UiConstants] Load Inventory UI Constants from Domain API succeeded', props()),
    loadFail: createAction('[NgRxInventory - UiConstants] Load Inventory UI Constants from Domain API failed', props()),
};

class InventoryUIConstantsEffects {
    constructor(actions$, httpService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        /**
         * Returns actions to populate the store based on successful/error api response
         *
         * @remarks
         * Responsible for making api call to fetch inventory ui constants
         */
        this.load$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_UI_CONSTANTS_ACTIONS.load), concatMap((action) => this.httpService
            .get(InventoryEndpoints.uiConstants, undefined)
            .pipe(map((inventoryUIConstants) => INVENTORY_UI_CONSTANTS_ACTIONS.loadSuccess({
            inventoryUIConstants,
        })), catchError((error) => of(INVENTORY_UI_CONSTANTS_ACTIONS.loadFail({ error })))))));
    }
}
InventoryUIConstantsEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryUIConstantsEffects, deps: [{ token: i1$1.Actions }, { token: InventoryHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
InventoryUIConstantsEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryUIConstantsEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryUIConstantsEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Actions }, { type: InventoryHttpClientService }]; } });

/* eslint-disable no-shadow */
const initialInventoryUIConstantsState = {
    inventoryUIConstants: {},
    error: undefined,
    loaded: false,
};
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _inventoryUIConstantsReducer = createReducer(initialInventoryUIConstantsState, on(INVENTORY_UI_CONSTANTS_ACTIONS.load, (state, action) => ({
    ...state,
    loaded: false,
    loading: true,
})), on(INVENTORY_UI_CONSTANTS_ACTIONS.loadSuccess, (state, action) => ({
    ...state,
    inventoryUIConstants: action.inventoryUIConstants,
    loaded: true,
    loading: false,
})), on(INVENTORY_UI_CONSTANTS_ACTIONS.loadFail, (state, action) => ({
    ...state,
    error: action.error,
    loaded: true,
    loading: false,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function inventoryUIConstantsReducer(state, action) {
    return _inventoryUIConstantsReducer(state, action);
}

class InventoryService {
    constructor(store, panAppState) {
        this.store = store;
        this.panAppState = panAppState;
    }
    initialize() {
        combineLatest([
            this.panAppState.getDistinctSelectedCustomer(),
            this.panAppState.feature$(['split_global_inventory']),
            this.store.select(selectShowInventory),
        ])
            .pipe(filter(([customer]) => customer?.selected))
            .subscribe(([customer, splitGlobalFlag, showInventoryFlag]) => {
            this.store.dispatch(INVENTORY_ACTIONS.resetStore());
            if (splitGlobalFlag && showInventoryFlag) {
                this.store.dispatch(INVENTORY_ACTIONS.load());
                this.store.dispatch(INVENTORY_UI_CONSTANTS_ACTIONS.load());
                this.store.dispatch(INVENTORY_ACTIONS.loadCustomerMigration());
            }
        });
    }
}
InventoryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryService, deps: [{ token: i1$2.Store }, { token: i2.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
InventoryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Store }, { type: i2.PanAppState }]; } });

const selectInventoryUIConstantsState = createFeatureSelector(INVENTORY_UI_CONSTANTS_KEY);
const inventoryUIConstants = () => createSelector(selectInventoryUIConstantsState, (uiConstants) => uiConstants);
const createInventoryInfo = () => createSelector(selectInventoryUIConstantsState, (uiConstants) => uiConstants.inventoryUIConstants?.createInventoryInfo);

class InventoryUIConstantsFacadeService {
    constructor(store) {
        this.store = store;
        this.inventoryUIConstants$ = () => this.store.select(inventoryUIConstants());
        this.createInventoryInfo$ = () => this.store.select(createInventoryInfo());
    }
    loadInventoryUIConstants() {
        this.store.dispatch(INVENTORY_ACTIONS.load());
    }
}
InventoryUIConstantsFacadeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryUIConstantsFacadeService, deps: [{ token: i1$2.Store }], target: i0.ɵɵFactoryTarget.Injectable });
InventoryUIConstantsFacadeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryUIConstantsFacadeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryUIConstantsFacadeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Store }]; } });

/* eslint-disable max-len */
const FOOD_COST_ACTIONS = {
    resetStore: createAction('[NgRxFoodCost - Food Cost] Reset Food Cost in store'),
    setRange: createAction('[NgRxFoodCost - Food Cost] Set Food Cost range in store', props()),
    create: createAction('[NgRxFoodCost - Food Cost] Create Food Cost from Inventory API', props()),
    createSuccess: createAction('[NgRxFoodCost - Food Cost] Create Food Cost Report from Inventory API succeeded', props()),
    createFail: createAction('[NgRxFoodCost - Food Cost] Create Food Cost Report from Inventory API failed', props()),
    delete: createAction('[NgRxFoodCost - Food Cost] Delete Food Cost from Inventory API', props()),
    deleteFail: createAction('[NgRxFoodCost - Food Cost] Delete Food Cost Report from Inventory API failed', props()),
    deleteSuccess: createAction('[NgRxFoodCost - Food Cost] Deleted Food Cost from Inventory API success', props()),
    load: createAction('[NgRxFoodCost - Food Cost] Load Food Cost Reports from Inventory API'),
    loadSuccess: createAction('[NgRxFoodCost - Food Cost] Load Food Cost Reports from Inventory API succeeded', props()),
    loadFail: createAction('[NgRxFoodCost - Food Cost] Load Food Cost Reports from Inventory API failed', props()),
    resetSort: createAction('[NgRxFoodCost - Food Cost] Reset Sort Options for Food Cost State'),
    setSort: createAction('[NgRxFoodCost - Food Cost] Set Sort Options for Food Cost State', props()),
    purchaseTotal: createAction('[NgRxFoodCost - Food Cost] Get Purchase Total', props()),
    purchaseTotalSuccess: createAction('[NgRxFoodCost - Food Cost] Get Purchase Total Success', props()),
    purchaseTotalFail: createAction('[NgRxFoodCost - Food Cost] Get Purchase Total Fail', props()),
};

var FoodCostSortEnum;
(function (FoodCostSortEnum) {
    FoodCostSortEnum["dateRangeDisplayText"] = "i18n.inventory.foodCost.dateRange";
    FoodCostSortEnum["dateRangeHeaderName"] = "daterange";
    FoodCostSortEnum["dateCreatedDisplayText"] = "i18n.inventory.foodCost.dateCreated";
    FoodCostSortEnum["dateCreatedHeaderName"] = "datecreated";
    FoodCostSortEnum["foodCostPercentageDisplayText"] = "i18n.inventory.foodCost.foodCostPercentage";
    FoodCostSortEnum["foodCostPercentageHeaderName"] = "foodcostpercentage";
    FoodCostSortEnum["targetDisplayText"] = "i18n.inventory.foodCost.targetText";
    FoodCostSortEnum["targetHeaderName"] = "target";
})(FoodCostSortEnum || (FoodCostSortEnum = {}));
const initialFoodCostSortOptions = {
    selectedHeader: FoodCostSortEnum.dateCreatedHeaderName,
    dateRange: {
        displayText: FoodCostSortEnum.dateRangeDisplayText,
        headerName: FoodCostSortEnum.dateRangeHeaderName,
        sortDirection: SortDirections.desc,
    },
    dateCreated: {
        displayText: FoodCostSortEnum.dateCreatedDisplayText,
        headerName: FoodCostSortEnum.dateCreatedHeaderName,
        sortDirection: SortDirections.desc,
    },
    foodCostPercentage: {
        displayText: FoodCostSortEnum.foodCostPercentageDisplayText,
        headerName: FoodCostSortEnum.foodCostPercentageHeaderName,
        sortDirection: SortDirections.desc,
    },
    target: {
        displayText: FoodCostSortEnum.targetDisplayText,
        headerName: FoodCostSortEnum.targetHeaderName,
        sortDirection: SortDirections.desc,
    },
};

/* eslint-disable max-len */
const foodCostReportAdapter = createEntityAdapter({
    selectId: (foodCostReportState) => foodCostReportState?.foodCostReportId,
});
const initialFoodCostState = foodCostReportAdapter.getInitialState({
    error: undefined,
    loaded: false,
    loading: false,
    sortOptions: initialFoodCostSortOptions,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _foodCostReducer = createReducer(initialFoodCostState, on(FOOD_COST_ACTIONS.resetStore, (state) => initialFoodCostState), on(FOOD_COST_ACTIONS.createSuccess, (state, action) => foodCostReportAdapter.upsertOne(action.foodCostReport, state)), on(FOOD_COST_ACTIONS.createFail, (state, action) => ({
    ...state,
    error: action?.error,
})), on(FOOD_COST_ACTIONS.load, (state, action) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(FOOD_COST_ACTIONS.loadSuccess, (state, action) => foodCostReportAdapter.upsertMany(action.foodCostReports, {
    ...state,
    loaded: true,
    loading: false,
})), on(FOOD_COST_ACTIONS.loadFail, (state, action) => ({
    ...state,
    loaded: true,
    loading: false,
    error: action?.error,
})), on(FOOD_COST_ACTIONS.resetSort, (state, action) => ({
    ...state,
    sortOptions: initialFoodCostSortOptions,
})), on(FOOD_COST_ACTIONS.setSort, (state, action) => {
    let newSortOptions = { ...initialFoodCostSortOptions };
    if (action.headerName !== state.sortOptions.selectedHeader) {
        newSortOptions = { ...newSortOptions, selectedHeader: action.headerName };
    }
    else {
        let lookupString;
        if (action.headerName === FoodCostSortEnum.dateCreatedHeaderName) {
            lookupString = 'dateCreated';
        }
        else if (action.headerName === FoodCostSortEnum.dateRangeHeaderName) {
            lookupString = 'dateRange';
        }
        else {
            lookupString = 'foodCostPercentage';
        }
        const currentOptions = { ...state.sortOptions[lookupString] };
        newSortOptions = {
            ...newSortOptions,
            [lookupString]: {
                ...currentOptions,
                sortDirection: currentOptions?.sortDirection === SortDirections.asc
                    ? SortDirections.desc
                    : SortDirections.asc,
            },
            selectedHeader: action.headerName,
        };
    }
    return {
        ...state,
        sortOptions: newSortOptions,
    };
}), on(FOOD_COST_ACTIONS.delete, (state, action) => ({
    ...state,
    loaded: false,
    loading: true,
})), on(FOOD_COST_ACTIONS.deleteSuccess, (state, action) => foodCostReportAdapter.removeOne(action.foodCostReportId, {
    ...state,
    loaded: true,
    loading: false,
})), on(FOOD_COST_ACTIONS.deleteFail, (state, action) => ({
    ...state,
    loaded: true,
    loading: false,
    error: action?.error,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function foodCostReducer(state, action) {
    return _foodCostReducer(state, action);
}

class FoodCostEffects {
    constructor(actions$, httpService, store) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.store = store;
        /**
         * @remarks
         * Responsible for making api call to create Food Cost Report
         */
        this.getFoodCostReports$ = createEffect(() => this.actions$.pipe(ofType(FOOD_COST_ACTIONS.load), switchMap((action) => this.httpService
            .get(InventoryEndpoints.foodCostReport)
            .pipe(switchMap((response) => [
            FOOD_COST_ACTIONS.loadSuccess({ foodCostReports: response }),
        ]), catchError((error) => of(FOOD_COST_ACTIONS.loadFail({ error })))))));
        /**
         * @remarks
         * Responsible for making api call to create Food Cost Report
         */
        this.createFoodCostReport$ = createEffect(() => this.actions$.pipe(ofType(FOOD_COST_ACTIONS.create), switchMap((action) => this.httpService
            .post(InventoryEndpoints.foodCostReport, action.reportFields)
            .pipe(switchMap((response) => [
            FOOD_COST_ACTIONS.createSuccess({ foodCostReport: response }),
        ]), catchError((error) => of(FOOD_COST_ACTIONS.createFail({ error })))))));
        /**
         * @remarks
         * Responsible for making api call to delete Food Cost Report
         */
        this.deleteFoodCostReport$ = createEffect(() => this.actions$.pipe(ofType(FOOD_COST_ACTIONS.delete), switchMap((action) => this.httpService
            .delete(InventoryEndpoints.foodCostReport, {
            params: {
                customerNumber: action?.customerNumber,
                divisionNumber: action?.divisionNumber,
                userType: action?.user?.userType,
                userName: action?.user?.userName,
                userId: action?.user?.userId,
                foodCostReportId: action?.foodCostReport?.foodCostReportId,
            },
        })
            .pipe(map(() => FOOD_COST_ACTIONS.deleteSuccess({
            foodCostReportId: action.foodCostReport.foodCostReportId,
            startPeriod: action.foodCostReport.startPeriod,
            endPeriod: action.foodCostReport.endPeriod,
        })), catchError((error) => of(FOOD_COST_ACTIONS.deleteFail({
            error,
            startPeriod: action.foodCostReport.startPeriod,
            endPeriod: action.foodCostReport.endPeriod,
        })))))));
        /**
         * @remarks
         * Responsible for making api call get Food Purchase Total
         */
        this.getFoodPurchaseTotal$ = createEffect(() => this.actions$.pipe(ofType(FOOD_COST_ACTIONS.purchaseTotal), switchMap((action) => {
            const queryParams = new Map();
            queryParams.set('departmentNumbers', action.departmentNumbers);
            queryParams.set('inventoryStartDate', action.inventoryStartDate);
            queryParams.set('inventoryEndDate', action.inventoryEndDate);
            if (!!action.excludeClass) {
                queryParams.set('excludeClass', action.excludeClass);
            }
            return this.httpService
                .get(InventoryEndpoints.foodPurchaseTotal, queryParams)
                .pipe(map((response) => FOOD_COST_ACTIONS.purchaseTotalSuccess({
                usfPurchaseTotal: response.usfPurchaseTotal,
            })), catchError((error) => of(FOOD_COST_ACTIONS.purchaseTotalFail({ error }))));
        })));
    }
}
FoodCostEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FoodCostEffects, deps: [{ token: i1$1.Actions }, { token: InventoryHttpClientService }, { token: i1$2.Store }], target: i0.ɵɵFactoryTarget.Injectable });
FoodCostEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FoodCostEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FoodCostEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Actions }, { type: InventoryHttpClientService }, { type: i1$2.Store }]; } });

class InventoryItemEffects {
    constructor(actions$, httpService, store) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.store = store;
        this.processPendingUpdates$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ITEM_ACTIONS.bulkItemUpdate, INVENTORY_ITEM_ACTIONS.bulkItemUpdateSuccess, INVENTORY_ITEM_ACTIONS.bulkItemUpdateFail, INVENTORY_ITEM_ACTIONS.processPendingItemUpdates), debounceTime(500), withLatestFrom(this.store.select(selectPendingItemUpdateState), this.store.select(selectInventoryEntities), this.store.select(selectInventoryOnline())), filter(([ignore1, ignore2, ignore3, isOnline]) => !!isOnline), mergeMap(([action, pendingUpdates, inventories]) => this.handlePendingUpdates(action, pendingUpdates, inventories))));
        this.checkPendingUpdates$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ITEM_ACTIONS.checkPendingUpdates), withLatestFrom(this.store.select(selectPendingItemUpdateState), this.store.select(selectInventoryEntities)), mergeMap(([_, pendingUpdates, inventories]) => this.checkForPendingUpdates(pendingUpdates))));
        /**
         * Effect that manages clearing missed items after timeout
         * If a new conflict occurs for an item before timeout, resets its timer
         */
        this.clearMissedItemUpdatesTimeout$ = createEffect(() => this.actions$.pipe(ofType(INVENTORY_ITEM_ACTIONS.bulkItemUpdateSuccess), filter((action) => !!action?.missedItems && action.missedItems.length > 0), 
        // Group by item key to handle conflicts for the same item
        mergeMap((action) => action.missedItems.map((missedItem) => ({
            key: generatePendingItemUpdateKey(missedItem),
            missedItem,
        }))), groupBy((item) => item.key), 
        // For each group (unique item), switch to a new timer when conflicts occur
        mergeMap((group) => group.pipe(switchMap((item) => timer(this.clearMissedItemUpdatesTimeout).pipe(map(() => item.missedItem))))), map((missedItem) => INVENTORY_ITEM_ACTIONS.clearMissedItemUpdatesAfterTimeout({
            missedItems: [missedItem],
        }))));
        this.maxRetryAttempts = 3;
        this.clearMissedItemUpdatesTimeout = 10000;
    }
    handlePendingUpdates(action, pendingUpdates, inventories) {
        const inventoryId = this.getInventoryIdFromAction(action);
        const inventory = inventories[inventoryId];
        if (!inventory) {
            return EMPTY;
        }
        const consolidatedByItem = this.consolidateItemUpdates(pendingUpdates, inventoryId);
        if (consolidatedByItem.size === 0) {
            return EMPTY;
        }
        const itemsMap = createInventoryItemLookup(inventory);
        const { failedUpdates, validUpdates } = this.separateUpdates(consolidatedByItem, itemsMap, inventoryId);
        const actions = [];
        if (failedUpdates.length > 0) {
            actions.push(of(INVENTORY_ITEM_ACTIONS.revertFailedUpdates({
                updates: failedUpdates,
            }), INVENTORY_ITEM_ACTIONS.clearFailedPendingUpdates({
                updates: failedUpdates,
            })));
        }
        if (validUpdates.length > 0) {
            actions.push(this.createUpdateActions(validUpdates, inventoryId, action));
        }
        return merge(...actions);
    }
    separateUpdates(consolidatedByItem, itemsMap, inventoryId) {
        const failedUpdates = [];
        const validUpdates = [];
        consolidatedByItem.forEach((consolidated, key) => {
            if (consolidated.hasExceededRetries) {
                failedUpdates.push({
                    inventoryId,
                    groupName: consolidated.groupName,
                    productNumber: consolidated.itemNumber,
                });
            }
            else {
                this.processValidUpdate(consolidated, key, itemsMap, validUpdates);
            }
        });
        return { failedUpdates, validUpdates };
    }
    checkForPendingUpdates(pendingUpdates) {
        const inventoryUpdatesMap = this.groupPendingUpdatesByInventory(pendingUpdates);
        const actions = this.createProcessPendingItemUpdatesActions(inventoryUpdatesMap);
        return actions.length ? actions : EMPTY;
    }
    groupPendingUpdatesByInventory(pendingUpdates) {
        const inventoryUpdatesMap = new Map();
        Object.entries(pendingUpdates).forEach(([key, state]) => {
            if (!!state && this.isValidPendingState(state)) {
                const { inventoryId, productNumber, groupName, } = parsePendingItemUpdateKey(key);
                if (!inventoryUpdatesMap.has(inventoryId)) {
                    inventoryUpdatesMap.set(inventoryId, new Map());
                }
                const inventoryData = inventoryUpdatesMap.get(inventoryId);
                const lookupKey = generatePendingItemUpdateKey({
                    inventoryId,
                    productNumber,
                    groupName,
                });
                const combinedItemData = this.combineItemData(state);
                inventoryData?.set(lookupKey, {
                    groupName,
                    itemNumber: productNumber,
                    itemData: combinedItemData,
                });
            }
        });
        return inventoryUpdatesMap;
    }
    mapUpdatesToKeys(updates, inventoryId) {
        return updates.map((update) => ({
            inventoryId,
            groupName: update.groupName,
            productNumber: update.productNumber,
        }));
    }
    getInventoryIdFromAction(action) {
        if ('bulkUpdatePayload' in action) {
            return action.bulkUpdatePayload.inventoryId;
        }
        if ('inventory' in action) {
            return action.inventory.inventoryId;
        }
        if ('inventoryId' in action) {
            return action.inventoryId;
        }
        return action.updates[0]?.inventoryId;
    }
    consolidateItemUpdates(pendingUpdates, inventoryId) {
        const consolidatedByItem = new Map();
        Object.entries(pendingUpdates).forEach(([key, state]) => {
            const { inventoryId: stateInventoryId, productNumber: itemNumber, groupName, } = parsePendingItemUpdateKey(key);
            if (!!state &&
                this.shouldProcessPendingUpdate(stateInventoryId, inventoryId, state)) {
                const hasExceededRetries = this.hasExceededRetries(state);
                const combinedItemData = this.combineItemData(state);
                consolidatedByItem.set(key, {
                    groupName,
                    itemNumber: Number(itemNumber),
                    itemData: combinedItemData,
                    hasExceededRetries,
                });
            }
        });
        return consolidatedByItem;
    }
    combineItemData(state) {
        return state.pendingItemUpdates.reduce((combined, pendingUpdate) => ({
            ...combined,
            ...pendingUpdate.itemData,
        }), {});
    }
    processValidUpdate(consolidated, key, itemsMap, validUpdates) {
        const currentItem = itemsMap.get(key);
        if (currentItem) {
            validUpdates.push({
                groupName: consolidated.groupName,
                productNumber: consolidated.itemNumber,
                itemData: consolidated.itemData,
                itemUpdateDtm: currentItem.item.itemUpdateDtm,
            });
        }
    }
    isValidPendingState(state) {
        return !!state && state?.pendingItemUpdates?.length > 0 && !state.isSyncing;
    }
    createProcessPendingItemUpdatesActions(inventoryUpdatesMap) {
        const actions = [];
        inventoryUpdatesMap.forEach((itemUpdates, inventoryId) => {
            if (itemUpdates.size > 0) {
                const updates = Array.from(itemUpdates.values());
                const tracking = this.createTrackingObject(inventoryId, updates);
                actions.push(INVENTORY_ITEM_ACTIONS.processPendingItemUpdates({
                    inventoryId,
                    tracking,
                }));
            }
        });
        return actions;
    }
    createUpdateActions(validUpdates, inventoryId, action) {
        const setSyncing$ = this.createSetSyncingAction(validUpdates, inventoryId, true);
        const apiCall$ = this.makeUpdateItemsApiCall(validUpdates, inventoryId, action);
        return concat(setSyncing$, apiCall$);
    }
    createSetSyncingAction(updates, inventoryId, isSyncing) {
        return of(INVENTORY_ITEM_ACTIONS.setBulkSyncing({
            updates: updates.map((update) => ({
                inventoryId,
                groupName: update.groupName,
                productNumber: update.productNumber,
            })),
            isSyncing,
        }));
    }
    makeUpdateItemsApiCall(validUpdates, inventoryId, action) {
        return this.httpService
            .put(InventoryEndpoints.updateItems, {
            inventoryId,
            items: validUpdates,
        })
            .pipe(map((response) => this.handleApiSuccess(response, inventoryId, action.tracking)), catchError((error) => this.handleApiError(error, validUpdates, inventoryId, action.tracking)));
    }
    shouldProcessPendingUpdate(stateInventoryId, inventoryId, state) {
        return (stateInventoryId === inventoryId &&
            !!state?.pendingItemUpdates &&
            state.pendingItemUpdates.length > 0 &&
            !state?.isSyncing);
    }
    hasExceededRetries(state) {
        return state.pendingItemUpdates.some((update) => update.retryCount >= this.maxRetryAttempts);
    }
    handleApiSuccess(response, inventoryId, tracking) {
        return INVENTORY_ITEM_ACTIONS.bulkItemUpdateSuccess({
            inventory: response.inventory,
            missedItems: response.missedItems.map((item) => ({
                inventoryId,
                groupName: item.groupName,
                productNumber: item.productNumber,
            })),
            lastUpdatedByUser: response.lastUpdatedByUser,
            tracking: tracking
                ? createSuccessTrackingObject(tracking, inventoryId)
                : undefined,
        });
    }
    handleApiError(error, validUpdates, inventoryId, tracking) {
        const updates = this.mapUpdatesToKeys(validUpdates, inventoryId);
        return of(INVENTORY_ITEM_ACTIONS.incrementRetryCount({
            updates,
        }), INVENTORY_ITEM_ACTIONS.setBulkSyncing({
            updates,
            isSyncing: false,
        }), INVENTORY_ITEM_ACTIONS.bulkItemUpdateFail({
            updates,
            error,
            tracking: tracking
                ? createSuccessTrackingObject(tracking, inventoryId)
                : undefined,
        }));
    }
    createTrackingObject(inventoryId, updates) {
        return {
            tracing: {
                data: {
                    traceContext: InventoryTracingConstants.inventory,
                    isEndOfTrace: false,
                    isStartOfTrace: true,
                    attributes: {
                        spanName: InventoryTracingConstants.updateItemsSpan,
                        event: InventoryTracingConstants.updateItemsEvent,
                        inventoryId,
                        updates,
                    },
                },
                transformFunc: updateInventoryItemTransform,
            },
        };
    }
}
InventoryItemEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryItemEffects, deps: [{ token: i1$1.Actions }, { token: InventoryHttpClientService }, { token: i1$2.Store }], target: i0.ɵɵFactoryTarget.Injectable });
InventoryItemEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryItemEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InventoryItemEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Actions }, { type: InventoryHttpClientService }, { type: i1$2.Store }]; } });

/* eslint-disable prefer-arrow/prefer-arrow-functions */
class NgRxInventoryModule {
    constructor(ngrxInventoryLibConfig, store, panAppState) {
        this.ngrxInventoryLibConfig = ngrxInventoryLibConfig;
        this.store = store;
        this.panAppState = panAppState;
    }
    static forRoot(ngRxInventoryLibConfig, store, panAppState) {
        return {
            ngModule: NgRxInventoryModule,
            providers: [
                {
                    provide: NGRX_INVENTORY_LIB_CONFIG,
                    useValue: ngRxInventoryLibConfig,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializeInventoryState,
                    deps: [InventoryService],
                    multi: true,
                },
            ],
        };
    }
}
NgRxInventoryModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxInventoryModule, deps: [{ token: NGRX_INVENTORY_LIB_CONFIG }, { token: i1$2.Store }, { token: i2.PanAppState }], target: i0.ɵɵFactoryTarget.NgModule });
NgRxInventoryModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: NgRxInventoryModule, imports: [i1$2.StoreFeatureModule, i1$2.StoreFeatureModule, i1$2.StoreFeatureModule, i1$1.EffectsFeatureModule] });
NgRxInventoryModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxInventoryModule, providers: [
        InventoryUIConstantsEffects,
        InventoryUIConstantsFacadeService,
        InventoryHttpClientService,
        InventoryMigrationHttpClientService,
        InventoryService,
        InventoryEffects,
        FoodCostEffects,
    ], imports: [StoreModule.forFeature(INVENTORY_UI_CONSTANTS_KEY, inventoryUIConstantsReducer),
        StoreModule.forFeature(INVENTORY_KEY, inventoryReducer),
        StoreModule.forFeature(FOOD_COST_KEY, foodCostReducer),
        EffectsModule.forFeature([
            InventoryUIConstantsEffects,
            InventoryEffects,
            InventoryItemEffects,
            FoodCostEffects,
        ])] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxInventoryModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        StoreModule.forFeature(INVENTORY_UI_CONSTANTS_KEY, inventoryUIConstantsReducer),
                        StoreModule.forFeature(INVENTORY_KEY, inventoryReducer),
                        StoreModule.forFeature(FOOD_COST_KEY, foodCostReducer),
                        EffectsModule.forFeature([
                            InventoryUIConstantsEffects,
                            InventoryEffects,
                            InventoryItemEffects,
                            FoodCostEffects,
                        ]),
                    ],
                    providers: [
                        InventoryUIConstantsEffects,
                        InventoryUIConstantsFacadeService,
                        InventoryHttpClientService,
                        InventoryMigrationHttpClientService,
                        InventoryService,
                        InventoryEffects,
                        FoodCostEffects,
                    ],
                    exports: [],
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_INVENTORY_LIB_CONFIG]
                }] }, { type: i1$2.Store }, { type: i2.PanAppState }]; } });
function initializeInventoryState(inventoryState) {
    const state = () => inventoryState.initialize();
    return state;
}

const selectFoodCostState = createFeatureSelector(FOOD_COST_KEY);
const selectFoodCostLoaded = () => createSelector(selectFoodCostState, (foodCost) => foodCost?.loaded);
const selectFoodCostLoading = () => createSelector(selectFoodCostState, (foodCost) => foodCost?.loading);
const selectFoodCostSortOptions = () => createSelector(selectFoodCostState, (foodCost) => foodCost?.sortOptions);
const { selectIds: selectFoodCostIds, selectEntities: selectFoodCostEntities, selectAll: selectFoodCostArray, } = foodCostReportAdapter.getSelectors(selectFoodCostState);

var FoodCostTarget;
(function (FoodCostTarget) {
    FoodCostTarget["onTrack"] = "i18n.inventory.foodCost.target.onTrack";
    FoodCostTarget["aboveTarget"] = "i18n.inventory.foodCost.target.aboveTarget";
    FoodCostTarget["belowTarget"] = "i18n.inventory.foodCost.target.belowTarget";
})(FoodCostTarget || (FoodCostTarget = {}));

/*
 * Public API Surface of ngrx-inventory
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ANALYTICS_ACTIONS, BASE_INVENTORY_UNITS, DATE_VALIDATOR_DEFAULT, FOOD_COST_ACTIONS, FoodCostSortEnum, FoodCostTarget, GroupPosition, INVENTORY_ACTIONS, INVENTORY_GROUP_ACTIONS, INVENTORY_ITEM_ACTIONS, INVENTORY_WORKSHEET_MODE_DEFAULT, IN_EDIT_GROUPS_MODE_DEFAULT, IN_EDIT_NAME_MODE_DEFAULT, InventoriesSortOptions, InventoryAnalyticsConstants, InventoryItemEditOptions, InventoryService, InventoryTracingConstants, InventoryUIConstantsFacadeService, InventoryWorksheetCountSortOptions, InventoryWorksheetEditSortOptions, NAME_EXISTS_DEFAULT, NAME_VALIDATOR_DEFAULT, NgRxInventoryModule, PENDING_UPDATE_KEY_DELIMITER, SELECTED_GROUP_DEFAULT, SELECTED_PRICE_DEFAULT, SHOW_COMPLETED_DEFAULT, SortDirections, allInventoriesHeaders, baseInventoryAnalyticsTransform, clearMissedItemUpdates, clickButtonTransformer, copyInventoryTransform, createFailureTrackingObject, createInventoryAnalyticsTransformer, createInventoryInfo, createInventoryItemLookup, createInventoryTransformer, createSuccessTrackingObject, deleteGroupDeleteItems, deleteGroupMoveItems, deleteInventoryTransformer, displayInventoryTransformer, downloadCostReportInventoryTransformer, downloadInventoryTransformer, extractOriginalValues, filterUndefinedFields, foodCostReducer, foodCostReportAdapter, generatePendingItemUpdateKey, getOriginalValuesForFailedUpdates, initialAllInventoriesSort, initialCustomerMigrationStatus, initialFoodCostSortOptions, initialRecentInventoriesSort, initializeInventoryState, inventoryAdapter, inventoryReducer, inventoryUIConstants, inventoryUIConstantsReducer, inventoryWorksheetCountHeaders, inventoryWorksheetEditHeaders, mergeInventoryStateWithAPIInformation, modalAnalyticsTransformer, pageLoadedTransformer, parsePendingItemUpdateKey, priceOverrideTransformer, processMissedItems, processRemainingUpdates, recentInventoriesHeaders, revertInventoryGroups, selectAllInventories, selectAllInventoriesSearchKey, selectFoodCostLoaded, selectFoodCostLoading, selectFoodCostSortOptions, selectFoodCostState, selectInventoriesWithCounts, selectInventoryById, selectInventoryItemModalOptions, selectInventoryOnline, selectInventoryOptionPrice, selectInventoryOptions, selectInventoryState, selectInventoryUIConstantsState, selectInventoryValidators, selectMissedItemUpdates, selectPendingItemUpdateState, updateInventoryGroups, updateInventoryItemTransform, updateInventoryQuantity, updateMissedItemUpdates, updatePendingItemState };
